import { useEffect, useRef, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Wand2, Download, RefreshCw, CheckCircle, ExternalLink, Eye, FileEdit, Save, Plus, Trash2, Sparkles, FileText, X, Upload, Share2, Copy, Calendar as CalendarIcon, Dice5, Globe, Users, HelpCircle, Tag, Quote, Image as ImageIcon, Clock, Type, BarChart3, BookText, Briefcase, Mail, Layers, Palette, Settings2, Zap, Monitor, Tablet, Smartphone, Undo2, Redo2, ChevronDown, ChevronLeft, ChevronRight, Edit3, ListChecks, Shuffle, Navigation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { IndustryEditor, hasIndustryEditor } from "../components/IndustryEditor";
import { ThemeEditor } from "../components/ThemeEditor";
import { MediaUploader } from "../components/MediaUploader";
import { CtaActionEditor } from "../components/CtaActionEditor";
import { NavEditor } from "../components/NavEditor";
import { SubpagesEditor } from "../components/SubpagesEditor";
import { BookingEditor } from "../components/BookingEditor";
import { ProjectInquiryEditor } from "../components/ProjectInquiryEditor";
import { StockVideoPicker } from "../components/StockVideoPicker";
import { EditorSection } from "../components/EditorSection";
import { SortableList, DragHandleIcon } from "../components/SortableList";
import { LogoSuggestionsPicker } from "../components/LogoSuggestionsPicker";
import { CompactProjectStatusStrip } from "../components/CompactProjectStatusStrip";
import { QaReportStrip } from "../components/QaReportStrip";
import { WhatsAppEditor } from "../components/WhatsAppEditor";
import { SubpageSuggestionsModal } from "../components/SubpageSuggestionsModal";
import { SectionRegenButton } from "../components/SectionRegenButton";
import { FloatingMiniPreview } from "../components/FloatingMiniPreview";

const ServiceImageTile = ({ projectId, index, url, onChanged }) => {
  const [rerolling, setRerolling] = useState(false);
  const handleReroll = async () => {
    setRerolling(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/reroll-image?slot=services_${index}`, null, { withCredentials: true });
      onChanged(res.data.url);
      toast.success("Neu gewürfelt");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Würfeln fehlgeschlagen");
    } finally {
      setRerolling(false);
    }
  };
  return (
    <div className="relative aspect-[4/3] rounded-sm overflow-hidden border border-border bg-muted/30 group" data-testid={`service-tile-${index}`}>
      {url ? <img src={url} alt={`Service ${index + 1}`} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">—</div>}
      <button
        type="button"
        onClick={handleReroll}
        disabled={rerolling}
        className="absolute inset-0 bg-black/0 group-hover:bg-black/55 flex items-center justify-center gap-1.5 text-white text-xs opacity-0 group-hover:opacity-100 transition-all disabled:opacity-100 disabled:bg-black/70"
        data-testid={`reroll-service-${index}-btn`}
      >
        <span className={rerolling ? "animate-spin" : ""}>🎲</span> {rerolling ? "würfelt..." : "würfeln"}
      </button>
    </div>
  );
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_INFO = {
  geplant: { label: "Geplant", color: "text-blue-600" },
  generierung: { label: "Wird generiert...", color: "text-yellow-600" },
  überprüfung: { label: "Bereit zur Überprüfung", color: "text-orange-600" },
  geliefert: { label: "Geliefert", color: "text-green-600" },
  fehler: { label: "Fehler", color: "text-red-600" },
};

// Normalisiert das nav-Objekt aus content.nav in kanonische Form mit sicheren
// Defaults — damit das UI nie undefined-Werte an Controlled-Inputs übergibt.
function normalizeNavCfg(nav) {
  const n = (nav && typeof nav === "object") ? nav : {};
  const action = (n.cta_action && typeof n.cta_action === "object")
    ? { type: n.cta_action.type || "default", value: n.cta_action.value || "" }
    : { type: "default", value: "" };
  return {
    variant: n.variant || "default",
    bg_color: n.bg_color || "",
    text_color: n.text_color || "",
    cta_text: n.cta_text || "",
    cta_action: action,
    cta_visible: n.cta_visible !== false,
    logo_text: n.logo_text || "",
    logo_size: n.logo_size || "md",
    link_order: Array.isArray(n.link_order) ? n.link_order : [],
  };
}


export default function WebsiteBuilderPage() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [industryProfile, setIndustryProfile] = useState(null);
  const [company, setCompany] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [genPhase, setGenPhase] = useState("");
  const [genProgress, setGenProgress] = useState(0);
  const [showVideoPicker, setShowVideoPicker] = useState(false);
  const [instructions, setInstructions] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  // Phase 4: Inline-Edit-Mode — User bearbeitet Texte direkt in der Vorschau.
  const [inlineEditMode, setInlineEditMode] = useState(false);
  const [currentPage, setCurrentPage] = useState("index");
  const [availablePages, setAvailablePages] = useState([]);
  const [customPagesMeta, setCustomPagesMeta] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [viewMode, setViewMode] = useState("preview"); // "preview" | "edit"
  const [editContent, setEditContent] = useState(null);
  // Quick-Mode: nur die wichtigsten Sektionen für Vertriebler. Pro: alles.
  const [editorMode, setEditorMode] = useState(() => {
    try { return localStorage.getItem("ws_editor_mode") || "quick"; } catch { return "quick"; }
  });
  const setEditorModePersist = (m) => {
    setEditorMode(m);
    try { localStorage.setItem("ws_editor_mode", m); } catch {}
  };
  const [savingContent, setSavingContent] = useState(false);
  const [aiLoadingPage, setAiLoadingPage] = useState(null);
  const [lastAddedCpIdx, setLastAddedCpIdx] = useState(null);
  // AI Create Page Modal
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiBrief, setAiBrief] = useState("");
  const [aiTitle, setAiTitle] = useState("");
  const [aiInNav, setAiInNav] = useState(true);
  const [aiCreating, setAiCreating] = useState(false);
  const [suggestingContent, setSuggestingContent] = useState(false);
  const [plannedSubpages, setPlannedSubpages] = useState([]);
  const [generatingSubpages, setGeneratingSubpages] = useState(false);
  // Mobile/Tablet/Desktop-Preview-Toggle (nur visuell, framed das iframe)
  const [previewDevice, setPreviewDevice] = useState(() => {
    try { return localStorage.getItem("ws_preview_device") || "desktop"; } catch { return "desktop"; }
  });
  const setPreviewDevicePersist = (d) => {
    setPreviewDevice(d);
    try { localStorage.setItem("ws_preview_device", d); } catch {}
  };
  const editorScrollRef = useRef(null);
  const [miniPreviewOpen, setMiniPreviewOpen] = useState(() => {
    try { return localStorage.getItem("ws_mini_preview_open") === "1"; } catch { return false; }
  });
  const [miniPreviewSize, setMiniPreviewSize] = useState(() => {
    try { return localStorage.getItem("ws_mini_preview_size") || "md"; } catch { return "md"; }
  });
  const setMiniPreviewSizePersist = (s) => {
    setMiniPreviewSize(s);
    try { localStorage.setItem("ws_mini_preview_size", s); } catch {}
  };
  const toggleMiniPreview = () => {
    setMiniPreviewOpen(o => {
      const next = !o;
      try { localStorage.setItem("ws_mini_preview_open", next ? "1" : "0"); } catch {}
      return next;
    });
  };
  // Split-View im Edit-Modus: Editor links + Live-Preview-Iframe direkt
  // rechts daneben. Floating-Mini-Preview wird dabei automatisch
  // deaktiviert (kein Doppel-Preview).
  const [splitView, setSplitView] = useState(() => {
    try { return localStorage.getItem("ws_split_view") !== "0"; } catch { return true; }
  });
  const toggleSplitView = () => {
    setSplitView(s => {
      const next = !s;
      try { localStorage.setItem("ws_split_view", next ? "1" : "0"); } catch {}
      return next;
    });
  };

  // Seiten-basiertes Editieren: User wählt eine Seite aus der Tab-Bar und der
  // Editor zeigt nur die Sektionen DIESER Seite. Werte: "globals" | "index" |
  // "impressum" | "datenschutz" | `custom:{slug}`.
  const [currentEditPage, setCurrentEditPage] = useState("index");
  const [showNewPageModal, setShowNewPageModal] = useState(false);
  // Map: welche EditorSection gehört zu welcher Seite (für conditional render)
  const PAGE_OF_SECTION = {
    theme: "globals",
    media: "globals",
    nav: "globals",
    hero: "index",
    stats: "index",
    about: "index",
    services: "index",
    industry: "index",
    "section-order": "index",
    testimonials: "index",
    pricing: "index",
    "team-members": "index",
    social: "index",
    contact: "index",
    "custom-pages": "custom-list",   // Übersicht alle Custom-Pages
    "subpages-ai": "custom-list",
    impressum: "impressum",
    datenschutz: "datenschutz",
  };
  // Beantwortet: soll die Editor-Sektion `testid` auf dem aktuellen Page-Tab
  // sichtbar sein? Sonderfall: Sektionen mit globalen Datenquellen (Team,
  // Testimonials, Services-Detail) werden zur Custom-Page verschoben wenn
  // eine Subpage mit passendem page_type existiert — statt auf Startseite
  // zu bleiben. Das passt zum Rendering-Verhalten im Backend.
  const SECTION_TO_PAGETYPE = {
    "team-members": "team",
    "testimonials": "testimonials",
    // services-detail, gallery, faq haben eigene page-scoped Editoren im
    // Custom-Page-Block → hier nicht duplizieren
  };
  const customPageMatchesSection = (testid) => {
    const pt = SECTION_TO_PAGETYPE[testid];
    if (!pt) return null;
    const cps = editContent.custom_pages || [];
    const match = cps.find(cp => detectPageType(cp) === pt);
    return match ? (match.slug || "") : null;
  };
  const showSection = (testid) => {
    const sectionPage = PAGE_OF_SECTION[testid] || "index";
    if (currentEditPage.startsWith("custom:")) {
      // Team-Mitglieder / Testimonials auf der passenden Subpage anzeigen
      const targetSlug = customPageMatchesSection(testid);
      if (targetSlug && currentEditPage === `custom:${targetSlug}`) return true;
      return sectionPage === "custom-list";
    }
    if (currentEditPage === "custom-list") return sectionPage === "custom-list";
    // Auf Startseite ausblenden wenn die Daten einer Subpage gehören
    if (currentEditPage === "index" && customPageMatchesSection(testid)) return false;
    return sectionPage === currentEditPage;
  };

  // Page-Type-Detection (analog page_layouts.py): Mappt Slug/Title auf
  // einen rendering-page-type damit der Editor weiß WELCHE Datenquelle
  // die rendered Subpage tatsächlich befüllt (z.B. "Referenzen" → testimonials).
  const detectPageType = (page) => {
    if (!page) return "rich-landing";
    const hay = `${(page.title || "").toLowerCase()} ${(page.slug || "").toLowerCase().replace(/[-_]/g, " ")}`;
    // Industry-Override: spezifische Slugs (Fahrschule etc.) müssen
    // generische page_types wie "services-detail" oder "rich-landing"
    // überschreiben — sonst zeigen alte Demos den falschen Editor.
    // (analog detect_page_type in backend/page_layouts.py)
    const FAHRSCHULE_OVERRIDE = [
      ["fahrschule-pricing", ["führerscheinklassen", "fuehrerscheinklassen", "preise & klassen", "klassen & preise"]],
      ["fahrschule-fleet",   ["schulungsfahrzeuge", "fahrzeugflotte", "unser fuhrpark", "schulwagen"]],
    ];
    const GENERIC_OVERRIDABLE = new Set(["", "rich-landing", "services-detail", "story", "gallery", "blog"]);
    const explicitType = (page.page_type || "").trim().toLowerCase();
    if (GENERIC_OVERRIDABLE.has(explicitType)) {
      for (const [forceType, tokens] of FAHRSCHULE_OVERRIDE) {
        if (tokens.some(t => hay.includes(t))) return forceType;
      }
    }
    if (page.page_type) return page.page_type;
    const TOKENS = [
      ["fahrschule-pricing", ["führerscheinklassen", "fuehrerscheinklassen", "preise & klassen", "klassen & preise"]],
      ["fahrschule-fleet",   ["schulungsfahrzeuge", "fahrzeugflotte", "unser fuhrpark", "schulwagen"]],
      ["careers",         ["karriere", "jobs", "stellen", "career", "ausbildung"]],
      ["directions",      ["anfahrt", "wegbeschreibung", "anreise", "directions", "standort"]],
      ["project-inquiry", ["projekt anfragen", "anfrage", "anfrageformular", "fahrt anfragen", "beratung anfragen"]],
      ["team",            ["team", "mitarbeiter", "crew", "stylisten", "fahrlehrer"]],
      ["faq",             ["faq", "häufige fragen", "fragen und antworten", "hilfe-center"]],
      ["testimonials",    ["testimonials", "kundenstimm", "referenz", "bewertung", "erfahrungsbericht"]],
      ["gallery",         ["galerie", "gallery", "portfolio", "arbeiten", "looks", "impressionen"]],
      ["services-detail", ["leistungsübersicht", "leistungen & preise", "preisliste"]],
      ["story",           ["geschichte", "unsere story", "about us", "historie"]],
      ["blog",            ["blog", "magazin", "aktuelles", "news", "ratgeber"]],
    ];
    for (const [type, tokens] of TOKENS) {
      if (tokens.some(t => hay.includes(t))) return type;
    }
    return "rich-landing";
  };
  // Page-Type → kurze Erklärung welche globale Datenquelle gerendert wird
  const PAGE_TYPE_INFO = {
    "testimonials":    { label: "Kundenstimmen-Layout", source: "testimonials", section: "testimonials", desc: 'Diese Seite zeigt automatisch deine Kundenstimmen als Karten-Grid. Die Inhalte bearbeitest du auf der Startseite unter "Kundenstimmen".' },
    "team":            { label: "Team-Layout", source: "team_members", section: "team-members", desc: 'Diese Seite zeigt automatisch deine Team-Mitglieder als Bio-Karten. Die Inhalte bearbeitest du auf der Startseite unter "Team-Mitglieder".' },
    "gallery":         { label: "Galerie-Layout", source: "gallery", section: null, desc: 'Diese Seite zeigt automatisch deine Galerie-Bilder als Portfolio-Grid. Lass die KI eine neue Galerie generieren oder pflege Items unter "Galerie & Projekt-Tiles" auf dieser Seite.' },
    "faq":             { label: "FAQ-Layout", source: "faqs", section: null, desc: "Diese Seite zeigt FAQ-Akkordeons. Die Fragen pflegst du im FAQs-Block dieser Seite weiter unten." },
    "careers":         { label: "Karriere-Layout", source: "page_data", section: null, desc: "Diese Seite zeigt offene Stellen + Bewerbungsformular." },
    "directions":      { label: "Anfahrt-Layout", source: "company", section: null, desc: "Diese Seite zeigt Adresse + Anfahrtsbeschreibung + Karte basierend auf den Firmen-Stammdaten." },
    "project-inquiry": { label: "Anfrage-Layout", source: "page_data", section: null, desc: "Diese Seite zeigt ein Projekt-Anfrage-Formular." },
    "services-detail": { label: "Leistungen-Detail-Layout", source: "services", section: "services", desc: 'Diese Seite zeigt deine Leistungen als ausführliche Detail-Liste. Bearbeitest du auf der Startseite unter "Leistungen".' },
    "story":           { label: "Story-Layout", source: "page_data", section: null, desc: "Diese Seite zeigt eine Geschichten-Erzählung mit Content-Blöcken." },
    "blog":            { label: "Blog-Layout", source: "page_data", section: null, desc: "Diese Seite zeigt eine Blog-Artikel-Übersicht." },
    "fahrschule-pricing": { label: "Fahrschul-Preis-Matrix", source: "auto-tier", section: null, desc: 'Diese Seite zeigt eine vollständige Preistabelle mit allen Führerscheinklassen (Klasse B, B197, A1, A2, BE, AM, …). Tier wird automatisch aus deiner Brand-DNA abgeleitet — kannst du unten manuell überschreiben.' },
    "fahrschule-fleet":   { label: "Schulungsfahrzeuge-Showcase", source: "auto-fleet", section: null, desc: "Diese Seite zeigt 5 Schulungsfahrzeuge passend zur Brand-DNA-Persona. Mix aus Schaltung/Automatik/Motorrad richtet sich automatisch nach den angebotenen Klassen." },
  };

  // Wenn currentEditPage wechselt → Live-Preview-Iframe auf passende Seite
  // umschalten, sofern es eine echte URL-Seite ist (nicht "globals").
  useEffect(() => {
    if (!id || !previewUrl) return;
    let target = "index";
    if (currentEditPage === "globals") target = currentPage; // globals: Preview unverändert
    else if (currentEditPage === "impressum") target = "impressum";
    else if (currentEditPage === "datenschutz") target = "datenschutz";
    else if (currentEditPage === "custom-list") target = currentPage;
    else if (currentEditPage.startsWith("custom:")) target = currentEditPage.slice(7);
    else target = "index";
    if (target !== currentPage) {
      const k = Date.now();
      setCurrentPage(target);
      setRefreshKey(k);
      setPreviewUrl(buildPreviewUrl(id, target, k, inlineEditMode));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEditPage]);

  // Auto-Fill einer Section per KI: passt die Inhalte an Branche + Brand-DNA an.
  // Wird nur ausgelöst wenn die Section beim Aktivieren EMPTY ist (keine Daten
  // vorhanden) — dann generieren wir branchenspezifische Defaults.
  // Beispiel: User aktiviert „Pakete & Preise" auf einer Fahrschule-Demo →
  // KI generiert „Grundausbildung 1.490€", „Theorie-Intensivkurs 199€" etc.
  const autoFillSection = async (key) => {
    // Map UI-Key → Backend-Section-Name
    const SECTION_KEY_MAP = {
      stats: "stats",
      about: "about",
      services: "services",
      team_members: "team_members",
      testimonials: "testimonials",
      pricing: "pricing",
      contact: "contact",
    };
    const backendKey = SECTION_KEY_MAP[key];
    if (!backendKey) return;
    // Section bereits gefüllt? → nicht überschreiben (User hat manuell gepflegt)
    const current = editContent[key];
    const isEmpty = Array.isArray(current)
      ? current.length === 0
      : !current || (typeof current === "object" && Object.values(current).every(v => !v));
    if (!isEmpty) return;
    const toastId = toast.loading(`KI füllt „${key}" passend zur Marke…`);
    try {
      const res = await axios.post(
        `${API}/projects/${id}/regenerate-section`,
        { section: backendKey, instructions: "" },
        { withCredentials: true, timeout: 120000 }
      );
      const data = res.data?.data;
      const extras = res.data?.extras || {};
      if (data === undefined || data === null) throw new Error("Keine Daten");
      setEditContent(prev => ({ ...prev, [key]: data, ...extras }));
      toast.dismiss(toastId);
      toast.success(`„${key}" mit KI befüllt — passt zur Branche!`);
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(err.response?.data?.detail || `KI-Auto-Fill für "${key}" fehlgeschlagen`);
    }
  };
  // Padding für Editor-Container damit die Live-Vorschau die Felder nicht überlagert.
  // Höhe des Editors bleibt gleich; Breite reduziert sich um die Mini-Preview-Breite + 20px Gap.
  const MP_PADDING = miniPreviewOpen ? (
    miniPreviewSize === "sm" ? "pr-[300px]" :
    miniPreviewSize === "lg" ? "pr-[460px]" :
    "pr-[380px]"
  ) : "";
  const [showLogoPicker, setShowLogoPicker] = useState(false);
  const [showSubpagesModal, setShowSubpagesModal] = useState(false);
  // Bauen-Modus vs. Generieren-Modus: Linkes Settings-Panel ist nach erster
  // Generierung standardmäßig eingeklappt → mehr Platz für Editor + Vorschau.
  // User-Override per Toggle, persistent via localStorage.
  const [settingsOpen, setSettingsOpen] = useState(() => {
    try {
      const saved = localStorage.getItem("ws_settings_open");
      if (saved !== null) return saved === "1";
    } catch {}
    return false; // Default: Bauen-Modus
  });
  const toggleSettings = () => {
    setSettingsOpen(o => {
      const next = !o;
      try { localStorage.setItem("ws_settings_open", next ? "1" : "0"); } catch {}
      return next;
    });
  };
  // Generieren-Modus: Wenn noch keine Vorschau existiert, Settings-Panel
  // automatisch öffnen damit der User KI-Anweisungen + Template + Generate
  // erreichen kann. Sobald die erste Demo gebaut ist → Bauen-Modus default.
  // Außerdem: bei `?settings=open` Query-Param explizit aufklappen (z.B.
  // wenn vom Lead-Detail-Modal „Demo bauen" → Builder navigiert wird).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (project && !previewUrl && !generating && project.status !== "generierung") setSettingsOpen(true);
  }, [project, previewUrl, generating]);
  useEffect(() => {
    if (searchParams.get("settings") === "open") {
      setSettingsOpen(true);
      try { localStorage.setItem("ws_settings_open", "1"); } catch {}
      // Param entfernen damit Refresh nicht erneut triggert
      const next = new URLSearchParams(searchParams);
      next.delete("settings");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // ── Undo/Redo: einfache Versionshistorie der letzten 20 Edits ──────────────
  // Wir speichern Snapshots des `editContent`-Objekts. Bei `setEditContent`
  // wird automatisch ein neuer Snapshot in den Stack gepusht (außer beim
  // Programmatic-Reset und beim Undo/Redo selbst).
  const editHistory = useRef({ stack: [], pointer: -1, suppress: false });
  const pushHistorySnapshot = (snap) => {
    if (editHistory.current.suppress) return;
    if (!snap) return;
    const { stack, pointer } = editHistory.current;
    // Wenn nicht am Ende → alles nach pointer verwerfen
    const trimmed = stack.slice(0, pointer + 1);
    trimmed.push(JSON.stringify(snap));
    while (trimmed.length > 20) trimmed.shift();
    editHistory.current = { stack: trimmed, pointer: trimmed.length - 1, suppress: false };
  };
  const handleUndo = () => {
    const { stack, pointer } = editHistory.current;
    if (pointer <= 0) return;
    const newPtr = pointer - 1;
    try {
      const snap = JSON.parse(stack[newPtr]);
      editHistory.current = { stack, pointer: newPtr, suppress: true };
      setEditContent(snap);
      setTimeout(() => { editHistory.current.suppress = false; }, 0);
      toast.success("Rückgängig gemacht");
    } catch { /* noop */ }
  };
  const handleRedo = () => {
    const { stack, pointer } = editHistory.current;
    if (pointer >= stack.length - 1) return;
    const newPtr = pointer + 1;
    try {
      const snap = JSON.parse(stack[newPtr]);
      editHistory.current = { stack, pointer: newPtr, suppress: true };
      setEditContent(snap);
      setTimeout(() => { editHistory.current.suppress = false; }, 0);
      toast.success("Wiederhergestellt");
    } catch { /* noop */ }
  };
  // Tastatur-Shortcuts (Cmd/Ctrl + Z / Shift+Z)
  useEffect(() => {
    if (viewMode !== "edit") return;
    const onKey = (e) => {
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const mod = isMac ? e.metaKey : e.ctrlKey;
      if (!mod) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      // In Inputs/Textareas Standard-Browser-Undo nicht überschreiben
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.key === "z" && !e.shiftKey) { e.preventDefault(); handleUndo(); }
      else if ((e.key === "z" && e.shiftKey) || e.key === "y") { e.preventDefault(); handleRedo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewMode]);

  // History-Snapshot debounced bei jeder Content-Änderung pushen
  useEffect(() => {
    if (!editContent) return;
    const t = setTimeout(() => pushHistorySnapshot(editContent), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editContent]);

  // ── Live-Patch: bei jeder Content-Änderung Patches an Mini-Preview-Iframe senden ──
  // Debounced 200ms, damit beim Tippen nicht jede Tastatureingabe Traffic verursacht.
  useEffect(() => {
    if ((!miniPreviewOpen && !splitView) || !editContent || viewMode !== "edit") return;
    const t = setTimeout(() => {
      // Versuche zuerst Split-View-Iframe, fallback auf Mini-Preview-Iframe
      const iframe = document.querySelector('[data-testid="split-preview-iframe"]')
                  || document.querySelector('[data-testid="mini-preview-iframe"]');
      const win = iframe?.contentWindow;
      if (!win) return;
      const patches = [];
      // Hero
      patches.push({ field: "hero.eyebrow", attr: "text", value: editContent.hero?.eyebrow || (company?.industry || "") });
      patches.push({ field: "hero.title", attr: "text", value: editContent.hero?.title || "" });
      patches.push({ field: "hero.subtitle", attr: "text", value: editContent.hero?.subtitle || "" });
      patches.push({ field: "hero.cta", attr: "text", value: editContent.hero?.cta || "" });
      // About
      patches.push({ field: "about.heading", attr: "text", value: editContent.about?.heading || "" });
      patches.push({ field: "about.text", attr: "text", value: editContent.about?.text || "" });
      // Services
      (editContent.services || []).slice(0, 6).forEach((s, i) => {
        patches.push({ field: `services.${i}.title`, attr: "text", value: s?.title || "" });
        patches.push({ field: `services.${i}.description`, attr: "text", value: s?.description || "" });
      });
      // Contact
      patches.push({ field: "contact.heading", attr: "text", value: editContent.contact?.heading || "" });
      // Theme — als CSS-Variablen-Patches
      const theme = editContent.theme || {};
      const themeVars = {};
      if (theme.bg)        themeVars["--bg"] = theme.bg;
      if (theme.fg)        themeVars["--fg"] = theme.fg;
      if (theme.accent)    themeVars["--accent"] = theme.accent;
      if (theme.nav_bg)    themeVars["--nav-bg"] = theme.nav_bg;
      if (theme.footer_bg) themeVars["--footer-bg"] = theme.footer_bg;
      if (theme.footer_fg) themeVars["--footer-fg"] = theme.footer_fg;
      if (Object.keys(themeVars).length) {
        patches.push({ attr: "theme", value: themeVars });
      }
      try { win.postMessage({ type: "live-patch", patches }, "*"); } catch {}
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editContent, miniPreviewOpen, viewMode, splitView]);

  // ── Auto-Scroll: bei Focus auf Editor-Inputs → Live-Preview springt zur Section ──
  // Pattern: jedes Input/Textarea hat data-testid wie "edit-hero-title", "edit-services-2-title".
  // Wir mappen das auf Section-Key + optional Feldspezifizierer (z.B. "services.2.title")
  // und feuern das gleiche `editor-section-open`-Event, das auch der TOC nutzt.
  // Debounced 120ms, damit Tab-Hopping zwischen Feldern nicht spammt.
  useEffect(() => {
    if ((!miniPreviewOpen && !splitView) || viewMode !== "edit") return;
    let lastSection = null;
    let lastField = null;
    let lastPage = null;
    let timer = null;
    const sectionFromTestid = (id) => {
      if (!id) return null;
      // Custom-Pages: testids wie "cp-hero-title-{i}", "cp-content-text-{i}",
      // "custom-page-title-{i}", "custom-page-slug-{i}", "cp-stat-value-{i}-{si}".
      // Wir lesen den letzten/relevanten Index, mappen auf den Slug der Page.
      const cpMatch = id.match(/^cp-[a-z]+-[a-z]+-(\d+)(?:-\d+)?$/) ||
                      id.match(/^custom-page-[a-z]+-(\d+)$/);
      if (cpMatch) {
        const idx = parseInt(cpMatch[1], 10);
        const cp = editContent?.custom_pages?.[idx];
        if (cp?.slug) {
          return { section: "custom-pages", page: cp.slug };
        }
        return { section: "custom-pages" };
      }
      if (!id.startsWith("edit-")) return null;
      const rest = id.slice(5);
      // Pattern für Service-Cards: existing "service-title-{i}" und "service-description-{i}"
      const svcMatch = rest.match(/^service-(title|description)-(\d+)$/);
      if (svcMatch) {
        return { section: "services", field: `services.${svcMatch[2]}.${svcMatch[1]}` };
      }
      // Impressum & Datenschutz → Sub-Page-Wechsel im Mini-Preview
      if (rest.startsWith("imp-")) return { section: "impressum" };
      if (rest.startsWith("ds-"))  return { section: "datenschutz" };
      // Generisches Mapping (Index-Page, nur Anchor-Scroll)
      const head = rest.split("-")[0];
      const KNOWN = {
        hero: "hero", about: "about", contact: "contact",
        stats: "stats", team: "team", trust: "trust", industry: "industry",
      };
      const section = KNOWN[head];
      if (!section) return null;
      const tail = rest.slice(head.length + 1);
      const field = section === "hero" ? `hero.${tail}`
        : section === "about" ? `about.${tail === "text" ? "text" : "heading"}`
        : section === "contact" ? "contact.heading"
        : null;
      return { section, field };
    };
    const onFocus = (e) => {
      const t = e.target;
      const id = t?.dataset?.testid || t?.getAttribute?.("data-testid");
      const mapped = sectionFromTestid(id);
      if (!mapped) return;
      if (mapped.section === lastSection && mapped.field === lastField && mapped.page === lastPage) return;
      lastSection = mapped.section;
      lastField = mapped.field;
      lastPage = mapped.page;
      clearTimeout(timer);
      timer = setTimeout(() => {
        window.dispatchEvent(new CustomEvent("editor-section-open", {
          detail: { testid: mapped.section, field: mapped.field, page: mapped.page },
        }));
      }, 120);
    };
    document.addEventListener("focusin", onFocus);
    return () => {
      document.removeEventListener("focusin", onFocus);
      clearTimeout(timer);
    };
  }, [miniPreviewOpen, viewMode, editContent, splitView]);

  // ── Click-on-Section in Live-Preview → Editor scrollt + öffnet Section ──
  // Iframe postet {type:"sls-section-click", section} bei jedem Klick auf
  // hero/services/etc. Parent reagiert mit dem gleichen editor-section-open
  // Event, das auch der TOC nutzt — und scrollt Editor-Container zur Section.
  useEffect(() => {
    if (viewMode !== "edit") return;
    const handler = (e) => {
      const m = e.data || {};
      if (m.type !== "sls-section-click" || !m.section) return;
      const sec = String(m.section);
      try { toast.success(`→ ${sec}`, { duration: 1200 }); } catch {}
      window.dispatchEvent(new CustomEvent("editor-section-open", {
        detail: { testid: sec },
      }));
      // Editor-Container zur Section scrollen (analog zu TOC-jumpTo)
      setTimeout(() => {
        const el = document.getElementById(`editor-section-${sec}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [viewMode]);

  const buildPreviewUrl = (projectId, page, key, edit = false) =>
    `${API}/projects/${projectId}/preview-html?page=${encodeURIComponent(page)}&t=${key}${edit ? "&edit=1" : ""}`;

  const loadPreview = async (projectId, page = "index") => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/preview-meta`, { withCredentials: true });
      const pages = res.data.available_pages || [];
      setAvailablePages(pages);
      setCustomPagesMeta(res.data.custom_pages || []);
      if (res.data.has_content) {
        const key = Date.now();
        setRefreshKey(key);
        setCurrentPage(page);
        setPreviewUrl(buildPreviewUrl(projectId, page, key, inlineEditMode));
      } else {
        setPreviewUrl("");
      }
    } catch (err) { /* no preview yet */ }
  };

  // ── Phase 4: Inline-Edit PostMessage-Bus ─────────────────────────────
  // Hört auf {type:"sls-inline-edit", path, value} vom Preview-Iframe und
  // aktualisiert editContent via Dotted-Path. Debounced Save (800ms) +
  // Preview-Refresh via refreshKey.
  const inlineSaveTimer = useRef(null);
  useEffect(() => {
    const handler = async (e) => {
      const msg = e.data;
      if (!msg || msg.type !== "sls-inline-edit") return;
      const path = String(msg.path || "").trim();
      const value = String(msg.value || "");
      if (!path) return;
      // Set editContent[path] via dotted-path navigation
      setEditContent(prev => {
        if (!prev) return prev;
        const root = JSON.parse(JSON.stringify(prev));
        const parts = path.split(".");
        let cur = root;
        for (let i = 0; i < parts.length - 1; i++) {
          const k = parts[i];
          const nextIsNum = /^\d+$/.test(parts[i + 1]);
          if (cur[k] === undefined || cur[k] === null) cur[k] = nextIsNum ? [] : {};
          cur = cur[k];
        }
        cur[parts[parts.length - 1]] = value;
        return root;
      });
      // Debounced Save
      if (inlineSaveTimer.current) clearTimeout(inlineSaveTimer.current);
      inlineSaveTimer.current = setTimeout(async () => {
        try {
          // Minimal patch: baue ein Delta, das NUR den geänderten Pfad enthält
          const parts = path.split(".");
          const patch = {};
          let cur = patch;
          for (let i = 0; i < parts.length - 1; i++) {
            const k = parts[i];
            const nextIsNum = /^\d+$/.test(parts[i + 1]);
            cur[k] = nextIsNum ? [] : {};
            cur = cur[k];
          }
          cur[parts[parts.length - 1]] = value;
          // Save via existing update endpoint (fast=true für Inline-Edit = nur Index)
          await axios.put(`${API}/projects/${id}/content?fast=true`, patch, { withCredentials: true });
          toast.success("Gespeichert", { duration: 1500 });
          setRefreshKey(k => k + 1);
        } catch (err) {
          console.error("Inline-save failed:", err);
          toast.error("Speichern fehlgeschlagen");
        }
      }, 800);
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [id]);

  // Inline-Edit-Mode an/aus → iframe neu laden mit/ohne ?edit=1
  useEffect(() => {
    if (!id) return;
    const key = Date.now();
    setRefreshKey(key);
    setPreviewUrl(buildPreviewUrl(id, currentPage, key, inlineEditMode));
  }, [inlineEditMode]);  // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, tRes] = await Promise.all([
          axios.get(`${API}/projects/${id}`, { withCredentials: true }),
          axios.get(`${API}/templates`, { withCredentials: true }),
        ]);
        const p = pRes.data;
        setProject(p);
        setTemplates(tRes.data);
        setTemplateId(p.template_id);
        // Migration: Alte Projekte hatten den gescrapten BESTEHENDE-WEBSITE-Block
        // direkt im ai_instructions Feld (oft truncated@500). Den blenden wir hier
        // aus, damit der User-Input wieder editierbar ist. Der Block läuft jetzt
        // intern über _scraped_data → _build_scraped_instructions_block().
        const aiText = (p.ai_instructions || "").trim();
        const looksLikeLegacyScrapeDump = aiText.startsWith("BESTEHENDE WEBSITE-INHALTE");
        setInstructions(looksLikeLegacyScrapeDump ? "" : aiText);
        const cRes = await axios.get(`${API}/companies/${p.company_id}`, { withCredentials: true });
        setCompany(cRes.data);
        // Industry-Profile detection (optional)
        try {
          const prof = await axios.get(`${API}/projects/${id}/industry-profile`, { withCredentials: true });
          if (prof.data?.detected) setIndustryProfile(prof.data);
        } catch (_) { /* no-op */ }
        if (p.status === "generierung") {
          attachToRunningGeneration();
        } else if (p.status !== "geplant") {
          // Auto-Rebuild damit alte Projekte die neue Nav-Logik bekommen (ohne KI-Aufruf)
          if (p.generated_content) {
            try { await axios.post(`${API}/projects/${id}/rebuild-preview`, {}, { withCredentials: true }); } catch (_) {}
          }
          await loadPreview(id, "index");
        }
      } catch (err) {
        toast.error("Fehler beim Laden");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const pollGeneration = async () => {
    // Poll status alle 1.5s, max 5 Min
    const startedAt = Date.now();
    const MAX_MS = 5 * 60 * 1000;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      await new Promise(r => setTimeout(r, 1500));
      if (Date.now() - startedAt > MAX_MS) throw new Error("Zeitüberschreitung (> 5 Min). Bitte später erneut versuchen.");
      let st;
      try {
        const r = await axios.get(`${API}/projects/${id}/generation-status`, { withCredentials: true, timeout: 15000 });
        st = r.data;
      } catch (_) {
        continue;
      }
      if (typeof st.progress === "number") setGenProgress(st.progress);
      if (st.phase) setGenPhase(st.phase);
      if (st.status === "überprüfung" || st.status === "geliefert") return true;
      if (st.status === "abgebrochen") {
        toast.info("Generierung abgebrochen");
        return false;
      }
      if (st.status === "fehler") throw new Error(st.error || "Generierung fehlgeschlagen");
    }
  };

  const finishGeneration = async () => {
    setGenProgress(100);
    setGenPhase("fertig");
    const p = await axios.get(`${API}/projects/${id}`, { withCredentials: true });
    setProject(p.data);
    try {
      const prof = await axios.get(`${API}/projects/${id}/industry-profile`, { withCredentials: true });
      setIndustryProfile(prof.data?.detected ? prof.data : null);
    } catch (_) { /* no-op */ }
    await loadPreview(id, "index");
    setCurrentPage("index");
    toast.success("Website erfolgreich generiert!");
  };

  // Läuft beim Öffnen des Builders bereits ein Build (z.B. direkt nach „Demo
  // bauen" im Lead) → an den laufenden Job anhängen statt leeren Zustand zeigen.
  const attachToRunningGeneration = async () => {
    setGenerating(true);
    setGenPhase("queued");
    setSettingsOpen(false);
    try {
      if (await pollGeneration()) await finishGeneration();
    } catch (err) {
      toast.error(err.message || "Generierung fehlgeschlagen");
    } finally {
      setGenerating(false);
      setTimeout(() => { setGenPhase(""); setGenProgress(0); }, 2000);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setGenPhase("queued");
    setGenProgress(5);
    setPreviewUrl("");
    try {
      if (templateId !== project.template_id) {
        await axios.put(`${API}/projects/${id}`, { template_id: templateId }, { withCredentials: true });
      }
      // Fire-and-forget: Backend startet Job, returned sofort 202. Wir pollen bis fertig.
      try {
        await axios.post(`${API}/projects/${id}/generate`, { ai_instructions: instructions }, { withCredentials: true, timeout: 60000 });
      } catch (err) {
        if (err.response?.status === 409) {
          toast.info("Generierung läuft bereits — wir verfolgen den Fortschritt");
        } else if (err.code === "ECONNABORTED" || /timeout/i.test(err.message || "")) {
          // Server hat trotzdem gestartet. Wir gehen zum Polling über (siehe unten).
          toast.info("Server antwortet langsam — Generierung läuft trotzdem im Hintergrund");
        } else {
          throw err;
        }
      }
      if (!(await pollGeneration())) return;
      await finishGeneration();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Generierung fehlgeschlagen");
    } finally {
      setGenerating(false);
      setCancelling(false);
      setTimeout(() => { setGenPhase(""); setGenProgress(0); }, 2000);
    }
  };

  const handleCancelGenerate = async () => {
    if (cancelling) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/projects/${id}/cancel-generation`, {}, { withCredentials: true, timeout: 10000 });
      toast.info("Abbruch angefordert — wird gleich beendet");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Abbruch fehlgeschlagen");
      setCancelling(false);
    }
  };

  const refreshPreview = () => {
    const key = Date.now();
    setRefreshKey(key);
    setPreviewUrl(buildPreviewUrl(id, currentPage, key, inlineEditMode));
  };

  const handlePageSwitch = (page) => {
    if (page === currentPage) return;
    setCurrentPage(page);
    setPreviewUrl(buildPreviewUrl(id, page, refreshKey, inlineEditMode));
  };

  const handleMarkDelivered = async () => {
    const res = await axios.put(`${API}/projects/${id}`, { status: "geliefert" }, { withCredentials: true });
    setProject(res.data);
    toast.success("Als geliefert markiert");
  };

  const handleDownload = () => {
    if (!previewUrl) return;
    const a = document.createElement("a");
    a.href = previewUrl;
    a.download = `${company?.name || "website"}-${project?.template_id || "site"}.html`;
    a.click();
  };

  // Public Share — bevorzugt schöne Slug-URL (z. B. /demo/salon-schmidt-berlin)
  // Token-URL als Fallback für ältere Projekte ohne public_slug
  const [shareOpen, setShareOpen] = useState(false);
  const publicShareUrl = project?.public_slug
    ? `${window.location.origin}/demo/${project.public_slug}`
    : (project?.public_token
      ? `${process.env.REACT_APP_BACKEND_URL}/api/public/preview/${project.public_token}`
      : "");

  const togglePublicShare = async (enabled, rotate = false) => {
    try {
      const res = await axios.put(`${API}/projects/${id}/public-share`, { enabled, rotate }, { withCredentials: true });
      setProject(p => ({
        ...p,
        public_enabled: res.data.public_enabled,
        public_token: res.data.public_token,
        public_slug: res.data.public_slug || p?.public_slug || "",
      }));
      toast.success(enabled ? "Öffentlicher Link aktiviert" : "Öffentlicher Link deaktiviert");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Aktion fehlgeschlagen");
    }
  };

  const copyShareLink = async () => {
    if (!publicShareUrl) return;
    try {
      await navigator.clipboard.writeText(publicShareUrl);
      toast.success("Link kopiert");
    } catch {
      toast.error("Kopieren fehlgeschlagen – bitte manuell markieren");
    }
  };

  const normalizeContent = (c) => ({
    hero: { title: "", subtitle: "", cta: "", eyebrow: "", variant: "image", video_url: "", particles_color: "#ffd84d", particles_density: 80, cta_action: { type: "default", value: "" }, ...(c?.hero || {}), cta_action: (c?.hero?.cta_action && typeof c.hero.cta_action === "object") ? { type: c.hero.cta_action.type || "default", value: c.hero.cta_action.value || "" } : { type: "default", value: "" } },
    nav: normalizeNavCfg(c?.nav),
    stats: (c?.stats && c.stats.length > 0 ? c.stats : [
      { value: "10+", label: "Jahre Erfahrung" },
      { value: "500+", label: "Zufriedene Kunden" },
      { value: "98%", label: "Weiterempfehlung" },
      { value: "24/7", label: "Erreichbarkeit" },
    ]).slice(0, 4).map(s => ({ value: s?.value || "", label: s?.label || "" })),
    about: { heading: "", text: "", ...(c?.about || {}) },
    services: (c?.services || []).map(s => ({ title: s?.title || "", description: s?.description || "" })),
    contact: { heading: "", email: "", phone: "", address: "", city: "", ...(c?.contact || {}) },
    legal: {
      impressum: {
        company_name: "", owner: "", address: "", phone: "", email: "",
        vat_id: "", register: "", register_court: "", responsible: "", disclaimer: "",
        ...(c?.legal?.impressum || {}),
      },
      datenschutz: {
        intro: "", text: "", ...(c?.legal?.datenschutz || {}),
      },
    },
    custom_pages: (c?.custom_pages || []).map(p => ({
      slug: p?.slug || "",
      title: p?.title || "",
      in_nav: p?.in_nav !== false,
      // Page-type discriminator + rich per-type payloads (preserve all!)
      page_type: p?.page_type || "",
      faqs: Array.isArray(p?.faqs) ? p.faqs : [],
      services_detail: Array.isArray(p?.services_detail) ? p.services_detail : [],
      testimonials: Array.isArray(p?.testimonials) ? p.testimonials : [],
      gallery: Array.isArray(p?.gallery) ? p.gallery : [],
      sections: Array.isArray(p?.sections) ? p.sections : [],
      posts: Array.isArray(p?.posts) ? p.posts : [],
      post: p?.post || undefined,
      // Rich structure (rich-landing)
      hero: {
        title: p?.hero?.title || p?.heading || "",
        subtitle: p?.hero?.subtitle || p?.intro || "",
        cta: p?.hero?.cta || "Jetzt kontaktieren",
        // Optionales Hero-Bild als Override (wird bei story-Layouts & rich-landing
        // genutzt; Fallback = content.images.about bzw. Branchen-Pack).
        image: p?.hero?.image || "",
        // CTA-Aktion: Was passiert beim Klick? (default|booking|inquiry|contact|phone|email|whatsapp|subpage|anchor|url|none)
        cta_action: (p?.hero?.cta_action && typeof p.hero.cta_action === "object")
          ? { type: p.hero.cta_action.type || "default", value: p.hero.cta_action.value || "" }
          : { type: "default", value: "" },
      },
      stats: (p?.stats && p.stats.length > 0 ? p.stats : [
        { value: "", label: "" },
        { value: "", label: "" },
        { value: "", label: "" },
        { value: "", label: "" },
      ]).slice(0, 4).map(s => ({ value: s?.value || "", label: s?.label || "" })),
      features: (p?.features && p.features.length > 0 ? p.features : [
        { title: "", description: "" },
        { title: "", description: "" },
        { title: "", description: "" },
      ]).slice(0, 4).map(f => ({ title: f?.title || "", description: f?.description || "" })),
      content: {
        heading: (typeof p?.content === "object" ? p?.content?.heading : "") || "",
        text: (typeof p?.content === "object" ? p?.content?.text : (typeof p?.content === "string" ? p?.content : "")) || "",
      },
      cta: {
        heading: p?.cta?.heading || "",
        button: p?.cta?.button || "Kontakt aufnehmen",
        action: (p?.cta?.action && typeof p.cta.action === "object")
          ? { type: p.cta.action.type || "default", value: p.cta.action.value || "" }
          : { type: "default", value: "" },
      },
      // Phase 3: per-subpage block toggles + order (neue Keys, konfliktfrei mit
      // existierendem `sections`-Array für rich-layout-pages).
      // Default: alle Blöcke sichtbar, Reihenfolge = [stats, features, content, cta].
      blocks_visible: (p?.blocks_visible && typeof p.blocks_visible === "object") ? p.blocks_visible : {},
      blocks_order: Array.isArray(p?.blocks_order) ? p.blocks_order : [],
    })),
    // Industry-spezifische Felder (werden nur bei den 4 Industry-Templates gerendert)
    menu: c?.menu || { intro: "", categories: [] },
    pricelist: c?.pricelist || { intro: "", categories: [] },
    schedule: c?.schedule || { note: "", classes: [] },
    memberships: c?.memberships || { note: "", tiers: [] },
    offers: c?.offers || { note: "", items: [] },
    hours: c?.hours || {},
    store_services: c?.store_services || { items: [] },
    // Testimonials (Kundenstimmen) – Array mit text + author
    testimonials: (c?.testimonials || []).map(t => ({
      text: t?.text || "",
      author: t?.author || "",
      role: t?.role || "",
      rating: typeof t?.rating === "number" ? t.rating : 5,
    })),
    // Pricing (3-6 Pakete)
    pricing: (c?.pricing || []).map(p => ({
      name: p?.name || "",
      price: p?.price || "",
      period: p?.period || "",
      description: p?.description || "",
      features: Array.isArray(p?.features) ? p.features : [],
      highlighted: !!p?.highlighted,
      cta: p?.cta || "",
    })),
    // Team-Members (Name + Rolle + Foto)
    team_members: (c?.team_members || []).map(m => ({
      name: m?.name || "",
      role: m?.role || "",
      bio: m?.bio || "",
      photo_url: m?.photo_url || "",
    })),
    // Footer Social-Links (Instagram/Facebook/Linkedin/...)
    social_links: c?.social_links || {},
    // WhatsApp-Schnellkontakt (Builder-Override) – muss beim Laden erhalten bleiben
    whatsapp: (c?.whatsapp && typeof c.whatsapp === "object") ? c.whatsapp : {},
    // Section Visibility (Toggles im Editor — default "enabled: true")
    // Struktur: { stats: {enabled: true}, testimonials: {enabled: false}, ... }
    sections: c?.sections || {},
    // Section-Reihenfolge (Phase 2 Drag&Drop). Default-Order wird von
    // `DEFAULT_SECTION_ORDER` bestimmt (Hero kommt immer zuerst, Footer immer
    // zuletzt). Beim Rendern respektieren NUR die 3 Premium-2026-Templates
    // diese Order — Legacy-Templates haben hartkodierte Reihenfolge.
    section_order: Array.isArray(c?.section_order) ? c.section_order : [],
    // Theme (Farben) + Preset-Key
    theme: c?.theme || {},
    theme_preset: c?.theme_preset || "",
    hero_variant: c?.hero_variant || "",
    // Media (Logo + Bilder, als Data-URL)
    logo_url: c?.logo_url || "",
    images: c?.images || {},
  });

  // Helper: Section-Toggle-State lesen. Default: true (alle Sektionen an)
  const isSectionEnabled = (key) =>
    editContent?.sections?.[key]?.enabled !== false;

  // Helper: Section-Toggle setzen → content.sections.{key}.enabled = bool
  const setSectionEnabled = (key, enabled) => {
    setEditContent(prev => ({
      ...prev,
      sections: { ...(prev.sections || {}), [key]: { ...(prev?.sections?.[key] || {}), enabled } },
    }));
  };

  const openEditor = () => {
    setEditContent(normalizeContent(project?.generated_content || project?.content));
    setViewMode("edit");
  };

  const updateField = (section, key, value) => {
    setEditContent(prev => ({ ...prev, [section]: { ...prev[section], [key]: value } }));
  };

  const updateService = (idx, key, value) => {
    setEditContent(prev => {
      const next = [...prev.services];
      next[idx] = { ...next[idx], [key]: value };
      return { ...prev, services: next };
    });
  };

  const updateStat = (idx, key, value) => {
    setEditContent(prev => {
      const next = [...prev.stats];
      next[idx] = { ...next[idx], [key]: value };
      return { ...prev, stats: next };
    });
  };

  const updateImpressum = (key, value) => {
    setEditContent(prev => ({ ...prev, legal: { ...prev.legal, impressum: { ...prev.legal.impressum, [key]: value } } }));
  };

  const updateDatenschutz = (key, value) => {
    setEditContent(prev => ({ ...prev, legal: { ...prev.legal, datenschutz: { ...prev.legal.datenschutz, [key]: value } } }));
  };

  const slugify = (s) => (s || "").toLowerCase().trim()
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);

  const addCustomPage = () => {
    setEditContent(prev => {
      const idx = prev.custom_pages.length + 1;
      const base = `seite-${idx}`;
      let slug = base;
      let n = idx;
      const existing = new Set(["index", "impressum", "datenschutz", "meilensteine", ...prev.custom_pages.map(p => p.slug)]);
      while (existing.has(slug)) { n += 1; slug = `seite-${n}`; }
      return {
        ...prev,
        custom_pages: [...prev.custom_pages, {
          slug,
          title: `Neue Seite ${idx}`,
          in_nav: true,
          hero: { title: "", subtitle: "", cta: "Jetzt kontaktieren" },
          stats: [{ value: "", label: "" }, { value: "", label: "" }, { value: "", label: "" }, { value: "", label: "" }],
          features: [{ title: "", description: "" }, { title: "", description: "" }, { title: "", description: "" }],
          content: { heading: "", text: "" },
          cta: { heading: "", button: "Kontakt aufnehmen" },
        }],
      };
    });
    setLastAddedCpIdx(editContent ? editContent.custom_pages.length : 0);
  };

  const removeCustomPage = (idx) => {
    setEditContent(prev => ({ ...prev, custom_pages: prev.custom_pages.filter((_, i) => i !== idx) }));
  };

  const updateCustomPage = (idx, key, value) => {
    setEditContent(prev => {
      const pages = [...prev.custom_pages];
      const updated = { ...pages[idx], [key]: value };
      // Auto-update slug when title changes and slug is empty/default
      if (key === "title") {
        const currentSlug = pages[idx].slug || "";
        if (!currentSlug || currentSlug.startsWith("seite-")) {
          const newSlug = slugify(value);
          const existing = new Set(["index", "impressum", "datenschutz", "meilensteine", ...pages.filter((_, i) => i !== idx).map(p => p.slug)]);
          if (newSlug && !existing.has(newSlug)) updated.slug = newSlug;
        }
      }
      pages[idx] = updated;
      return { ...prev, custom_pages: pages };
    });
  };

  const updateCustomPageNested = (idx, section, key, value) => {
    setEditContent(prev => {
      const pages = [...prev.custom_pages];
      pages[idx] = { ...pages[idx], [section]: { ...pages[idx][section], [key]: value } };
      return { ...prev, custom_pages: pages };
    });
  };

  const updateCustomPageArrayItem = (idx, arrayKey, itemIdx, key, value) => {
    setEditContent(prev => {
      const pages = [...prev.custom_pages];
      const arr = [...(pages[idx][arrayKey] || [])];
      arr[itemIdx] = { ...arr[itemIdx], [key]: value };
      pages[idx] = { ...pages[idx], [arrayKey]: arr };
      return { ...prev, custom_pages: pages };
    });
  };

  // Phase 3: per-subpage block toggles + order
  const CP_DEFAULT_BLOCK_ORDER = ["stats", "features", "content", "cta", "faqs", "gallery"];
  const isCpBlockOn = (pageIdx, blockKey) => {
    const bv = editContent?.custom_pages?.[pageIdx]?.blocks_visible || {};
    return bv[blockKey] !== false;
  };
  const setCpBlockOn = (pageIdx, blockKey, enabled) => {
    setEditContent(prev => {
      const pages = [...prev.custom_pages];
      pages[pageIdx] = {
        ...pages[pageIdx],
        blocks_visible: { ...(pages[pageIdx].blocks_visible || {}), [blockKey]: enabled },
      };
      return { ...prev, custom_pages: pages };
    });
  };
  const getCpBlockOrder = (pageIdx) => {
    const cp = editContent?.custom_pages?.[pageIdx];
    if (!cp) return CP_DEFAULT_BLOCK_ORDER;
    const valid = (cp.blocks_order || []).filter(k => CP_DEFAULT_BLOCK_ORDER.includes(k));
    return [...valid, ...CP_DEFAULT_BLOCK_ORDER.filter(k => !valid.includes(k))];
  };
  const setCpBlockOrder = (pageIdx, newOrder) => {
    setEditContent(prev => {
      const pages = [...prev.custom_pages];
      pages[pageIdx] = { ...pages[pageIdx], blocks_order: newOrder };
      return { ...prev, custom_pages: pages };
    });
  };

  // FAQs-Editor-Helpers pro Subpage
  const addCpFaq = (pageIdx) => {
    setEditContent(prev => {
      const pages = [...prev.custom_pages];
      pages[pageIdx] = { ...pages[pageIdx], faqs: [...(pages[pageIdx].faqs || []), { q: "", a: "" }] };
      return { ...prev, custom_pages: pages };
    });
  };
  const removeCpFaq = (pageIdx, faqIdx) => {
    setEditContent(prev => {
      const pages = [...prev.custom_pages];
      pages[pageIdx] = { ...pages[pageIdx], faqs: (pages[pageIdx].faqs || []).filter((_, i) => i !== faqIdx) };
      return { ...prev, custom_pages: pages };
    });
  };

  const generateCustomPageAI = async (idx) => {
    // Legacy helper — not used in the new rich editor UI, kept for backwards compat if needed
    const page = editContent.custom_pages[idx];
    if (!page?.title) { toast.error("Bitte zuerst einen Seitentitel eingeben"); return; }
    setAiLoadingPage(idx);
    try {
      const res = await axios.post(`${API}/projects/${id}/generate-custom-page`, { title: page.title, instructions: "" }, { withCredentials: true, timeout: 180000 });
      setEditContent(prev => {
        const pages = [...prev.custom_pages];
        pages[idx] = { ...pages[idx], content: { ...pages[idx].content, heading: res.data.heading || pages[idx].content.heading, text: res.data.content || pages[idx].content.text } };
        return { ...prev, custom_pages: pages };
      });
      toast.success("Content neu generiert");
    } catch (err) { toast.error(err.response?.data?.detail || "KI-Generierung fehlgeschlagen"); }
    finally { setAiLoadingPage(null); }
  };

  const addService = () => {
    setEditContent(prev => ({ ...prev, services: [...prev.services, { title: "", description: "" }] }));
  };

  const removeService = (idx) => {
    setEditContent(prev => ({ ...prev, services: prev.services.filter((_, i) => i !== idx) }));
  };

  const handleGenerateSubpages = async () => {
    const valid = plannedSubpages.filter(s => (s.title || "").trim() && (s.briefing || "").trim().length >= 5);
    if (valid.length === 0) { toast.error("Mindestens ein gültiger Eintrag (Titel + Briefing mit 5+ Zeichen) nötig"); return; }
    setGeneratingSubpages(true);
    try {
      const res = await axios.post(`${API}/projects/${id}/generate-subpages`, { pages: valid }, { withCredentials: true, timeout: 180000 });
      toast.success(`${res.data.added} Unterseite${res.data.added !== 1 ? "n" : ""} generiert${res.data.failed ? ` (${res.data.failed} fehlgeschlagen)` : ""}`);
      setPlannedSubpages([]);
      // Reload project + refresh preview
      const p = await axios.get(`${API}/projects/${id}`, { withCredentials: true });
      setProject(p.data);
      setEditContent(normalizeContent(p.data.generated_content || {}));
      refreshPreview();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generierung fehlgeschlagen");
    } finally {
      setGeneratingSubpages(false);
    }
  };

  const handleSuggestContent = async () => {
    if (!templateId) { toast.error("Bitte zuerst ein Template wählen"); return; }
    setSuggestingContent(true);
    try {
      const res = await axios.post(
        `${API}/projects/${id}/suggest-content`,
        { ai_instructions: instructions },
        { withCredentials: true, timeout: 180000 }
      );
      setEditContent(normalizeContent(res.data.content));
      toast.success("KI-Vorschläge eingefügt – prüfen, anpassen und speichern.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "KI-Vorschlag fehlgeschlagen");
    } finally {
      setSuggestingContent(false);
    }
  };

  const handleSaveContent = async () => {
    if (!editContent) return;
    // Bug-Fix B: Reservierte Titel blockieren statt nur zu warnen.
    const RESERVED = ["impressum", "datenschutz", "meilensteine", "startseite", "home"];
    const offending = (editContent.custom_pages || []).find(p =>
      RESERVED.includes((p.title || "").toLowerCase().trim())
    );
    if (offending) {
      toast.error(`„${offending.title}" ist ein reservierter Titel. Bitte umbenennen — sonst entstehen Duplikate im Footer.`);
      return;
    }
    // Slug-Duplikat-Check
    const slugs = (editContent.custom_pages || []).map(p => p.slug).filter(Boolean);
    const dup = slugs.find((s, i) => slugs.indexOf(s) !== i);
    if (dup) {
      toast.error(`URL-Slug „/${dup}" wird mehrfach verwendet. Bitte eindeutig vergeben.`);
      return;
    }
    setSavingContent(true);
    try {
      // Smart-Patch: nur betroffene Pages neu rendern (5x schneller bei reinen
      // Index-Änderungen wie Theme/Hero/Bilder/Services). Volle Rebuild greift
      // automatisch im Backend, sobald custom_pages/legal/team/career drin sind.
      const res = await axios.put(`${API}/projects/${id}/content?fast=true`, editContent, { withCredentials: true });
      setProject(res.data);
      await loadPreview(id, currentPage);
      setViewMode("preview");
      toast.success("Inhalte gespeichert und Vorschau aktualisiert");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSavingContent(false);
    }
  };

  const openAiCreateModal = () => {
    setAiBrief("");
    setAiTitle("");
    setAiInNav(true);
    setAiModalOpen(true);
  };

  const handleAiCreatePage = async () => {
    if (aiBrief.trim().length < 5) {
      toast.error("Bitte beschreibe die Seite in mindestens einem kurzen Satz.");
      return;
    }
    setAiCreating(true);
    try {
      const res = await axios.post(
        `${API}/projects/${id}/ai-create-page`,
        { brief: aiBrief.trim(), title: aiTitle.trim(), in_nav: aiInNav },
        { withCredentials: true, timeout: 180000 }
      );
      const newSlug = res.data.slug;
      setAiModalOpen(false);
      // Reload project + preview, switch to new page
      const pRes = await axios.get(`${API}/projects/${id}`, { withCredentials: true });
      setProject(pRes.data);
      await loadPreview(id, newSlug);
      toast.success(`Seite „${res.data.page.title}" erstellt`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "KI-Generierung fehlgeschlagen");
    } finally {
      setAiCreating(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">Laden...</div>;
  if (!project || !company) return <div className="text-center py-12 text-sm text-muted-foreground">Projekt nicht gefunden</div>;

  const statusInfo = STATUS_INFO[project.status] || STATUS_INFO.geplant;
  const template = templates.find(t => t.id === templateId);

  return (
    <div className="h-full flex flex-col space-y-4 max-w-full">
      {/* Header */}
      <div className="flex items-center gap-4 shrink-0">
        <Link to={`/firmen/${company.id}`} className="p-1.5 rounded-sm hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft size={16} />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-0.5">
            <Link to={`/firmen/${company.id}`} className="hover:text-foreground transition-colors">{company.name}</Link>
            <span>/</span>
            <span>Builder</span>
          </div>
          <h1 className="font-heading text-xl font-black tracking-tight truncate">{project.name}</h1>
        </div>
        <div className="flex items-center gap-3">
          {industryProfile && (
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm(`Branchen-Profil „${industryProfile.label}" anwenden?\n\nDas aktiviert: ${industryProfile.features.join(", ")}\nund legt ${industryProfile.suggested_pages.length} passende Unterseiten in die Warteschlange.\n\nBestehende Buchungs-/Anfrage-Einstellungen werden ÜBERSCHRIEBEN.`)) return;
                try {
                  const res = await axios.post(`${API}/projects/${id}/apply-industry-profile`, {}, { withCredentials: true });
                  toast.success(`Profil „${res.data.profile_label}" angewendet — ${res.data.subpages_queued} Unterseiten in Queue.`);
                  setIndustryProfile({ ...industryProfile, is_applied: true });
                } catch (e) { toast.error(e.response?.data?.detail || "Profil anwenden fehlgeschlagen"); }
              }}
              className={`hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition ${
                industryProfile.is_applied ? "bg-primary/10 border-primary/30 text-primary" : "bg-muted border-border text-muted-foreground hover:border-primary/40 hover:text-foreground"
              }`}
              title={industryProfile.is_applied ? "Profil aktiv — klicken um neu anzuwenden" : "Klicken um Branchen-Profil anzuwenden"}
              data-testid="industry-profile-badge"
            >
              <Sparkles size={10} />
              {industryProfile.label}
              {industryProfile.is_applied && <span className="text-primary">✓</span>}
              <span className="opacity-60">· {industryProfile.features.length} Features</span>
            </button>
          )}
          {project?._brand_dna?.persona && (
            <div className="hidden md:inline-flex items-center gap-1">
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border bg-muted border-border text-muted-foreground"
                style={{ borderColor: project._brand_dna?.palette?.accent + "60" }}
                title={`Brand-DNA — Persona: ${project._brand_dna.persona_label}\nFarbschema: ${project._brand_dna.palette_id}\nHero-Stil: ${project._brand_dna.hero_treatment}\nVariante: v${project._brand_dna.variant ?? 0}\nQuelle: ${project._brand_dna.source === "scraped" ? "aus Website-Inhalt erkannt" : "deterministisch aus Firmen-ID"}`}
                data-testid="brand-dna-badge"
              >
                <span
                  className="w-2.5 h-2.5 rounded-full inline-block"
                  style={{ background: project._brand_dna?.palette?.accent }}
                />
                <span className="capitalize">{project._brand_dna.persona_label}</span>
                <span className="opacity-60">· {project._brand_dna.hero_treatment}</span>
                {(project._brand_dna.variant ?? 0) > 0 && (
                  <span className="opacity-60">· v{project._brand_dna.variant}</span>
                )}
              </span>
              {(() => {
                const spec = project?.generated_content?._design_spec || project?.content?._design_spec;
                return (project?.template_id === "ai-composer" && spec) ? (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-semibold bg-primary/10 text-primary"
                    title={spec.rationale || "KI-Composer Design-Spec"}
                    data-testid="composer-spec-badge"
                  >
                    KI-Composer · {spec.persona} · {spec.palette} · {spec.font_pair}
                  </span>
                ) : null;
              })()}
              <button
                type="button"
                onClick={async () => {
                  try {
                    const res = await axios.post(`${API}/projects/${id}/randomize-variant`, {}, { withCredentials: true });
                    toast.success(`Variante v${res.data.variant} — ${res.data.brand_dna.persona_label} · ${res.data.brand_dna.palette_id}${res.data.template_changed ? ` · Template: ${res.data.template_id}` : ""}`);
                    // Reload project so badge + theme + preview refresh
                    setProject(p => ({ ...p, _brand_dna: res.data.brand_dna, template_id: res.data.template_id, content: { ...(p?.content || {}), _design_spec: res.data.design_spec || p?.content?._design_spec, _brand_dna: res.data.brand_dna, theme: res.data.brand_dna.palette ? { accent: res.data.brand_dna.palette.accent, bg: res.data.brand_dna.palette.bg, footer_bg: res.data.brand_dna.palette.footer_bg, text: res.data.brand_dna.palette.text } : (p?.content?.theme), hero: { ...(p?.content?.hero || {}), variant: res.data.brand_dna.hero_treatment } } }));
                    setTemplateId(res.data.template_id);
                    await loadPreview(id, currentPage || "index");
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Variante würfeln fehlgeschlagen");
                  }
                }}
                className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-border bg-card hover:bg-muted hover:border-primary/40 transition group"
                title="Variante würfeln — neue Persona, Palette, Hero & Template (Inhalte bleiben)"
                data-testid="randomize-variant-btn"
              >
                <Dice5 size={12} className="text-muted-foreground group-hover:text-primary group-hover:rotate-180 transition-transform duration-300" />
              </button>
            </div>
          )}
          <span className={`text-xs font-medium ${statusInfo.color}`}>{statusInfo.label}</span>
          {project.status === "überprüfung" && (
            <Button size="sm" variant="outline" onClick={handleMarkDelivered} className="rounded-sm gap-1.5 text-xs" data-testid="mark-delivered-btn">
              <CheckCircle size={12} /> Als geliefert markieren
            </Button>
          )}
        </div>
      </div>

      {/* "Demo vorbereitet" Banner — Schritt 2 des 2-Schritt-Workflows.
          Sichtbar wenn Project in stage="prepared" ist UND noch keine echte
          KI-generierte Demo gebaut wurde (`_ai_used=false`). Visualisiert dem
          Vertriebler: jetzt Template wählen + Subpages auswählen + Generieren. */}
      {project?.stage === "prepared" && !project?._ai_used && !generating && project?.status !== "generierung" && (
        <div className="rounded-sm border-2 border-dashed border-violet-400/60 bg-gradient-to-br from-violet-50/60 to-indigo-50/30 dark:from-violet-950/30 dark:to-indigo-950/20 px-4 py-3" data-testid="prepared-banner">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-md bg-violet-600 text-white shrink-0">
              <Sparkles size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm text-violet-900 dark:text-violet-200">
                Daten gesammelt — bereit zum Bauen 🎨
              </div>
              <p className="text-xs text-violet-800/80 dark:text-violet-300/80 mt-0.5">
                Wir haben die alte Website gescrapt und Branchen-Vorschläge geladen. Jetzt:
                <strong className="text-violet-900 dark:text-violet-200"> Template wählen</strong>
                {" → "}
                <strong className="text-violet-900 dark:text-violet-200">Subpages auswählen</strong>
                {" → "}
                <strong className="text-violet-900 dark:text-violet-200">„Neu erstellen" klicken</strong>.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSettingsOpen(true)}
              className="shrink-0 px-3 py-1.5 rounded-md bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700 transition-colors"
              data-testid="prepared-banner-open-settings"
            >
              Einstellungen öffnen →
            </button>
          </div>
        </div>
      )}

      {/* Compact Status-Strip — vereint Industry-Profile + Scraped-Summary in einer
          schmalen Zeile. Detail per Klick aufklappbar. Spart vertikalen Platz fürs
          Editing/Vorschau-Fenster. */}
      {(industryProfile || project?.scraped_summary?.available || (project?.requested_subpages && project.requested_subpages.length > 0)) && (
        <CompactProjectStatusStrip
          industryProfile={industryProfile}
          setIndustryProfile={setIndustryProfile}
          scrapedSummary={project?.scraped_summary}
          requestedSubpages={project?.requested_subpages || []}
          projectId={id}
          API={API}
          setProject={setProject}
          canApply={Object.keys(project?.generated_content || {}).length > 0 && !generating}
        />
      )}

      {(project?.generated_content || project?.content) && !generating && (
        <QaReportStrip
          qa={project?._qa}
          projectId={id}
          API={API}
          onUpdated={(next) => {
            setProject(next);
            if (typeof next === "object" && next) loadPreview(id, currentPage || "index");
          }}
        />
      )}

      {/* Industry-Profile Auto-Apply Banner — DEPRECATED: ersetzt durch CompactProjectStatusStrip */}
      {false && industryProfile && (
        <div
          className={`rounded-sm border px-4 py-3 flex items-start gap-3 mb-0 ${
            industryProfile.is_applied
              ? "border-primary/30 bg-primary/5"
              : "border-amber-400/40 bg-amber-50/70 dark:bg-amber-500/10"
          }`}
          data-testid="industry-profile-banner"
        >
          <Sparkles size={16} className={industryProfile.is_applied ? "text-primary mt-0.5 shrink-0" : "text-amber-600 dark:text-amber-400 mt-0.5 shrink-0"} />
          <div className="flex-1 min-w-0">
            {industryProfile.is_applied ? (
              <>
                <p className="text-sm font-semibold text-foreground">
                  Branchen-Profil „{industryProfile.label}" aktiv
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {industryProfile.features.length} Features aktiviert:&nbsp;
                  <span className="text-foreground">{industryProfile.features.join(" · ")}</span>
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold text-foreground">
                  Branche „{industryProfile.label}" erkannt
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Smart-Defaults verfügbar:&nbsp;
                  <span className="text-foreground">{industryProfile.features.join(" · ")}</span>
                  {" "}&middot; {industryProfile.suggested_pages.length} passende Unterseiten
                </p>
              </>
            )}
          </div>
          {!industryProfile.is_applied && (
            <Button
              size="sm"
              variant="default"
              className="rounded-sm text-xs shrink-0"
              data-testid="industry-profile-apply-btn"
              onClick={async () => {
                if (!window.confirm(`Branchen-Profil „${industryProfile.label}" anwenden?\n\nDas aktiviert: ${industryProfile.features.join(", ")}\nund legt ${industryProfile.suggested_pages.length} passende Unterseiten in die Warteschlange.\n\nBestehende Buchungs-/Anfrage-Einstellungen werden ÜBERSCHRIEBEN.`)) return;
                try {
                  const res = await axios.post(`${API}/projects/${id}/apply-industry-profile`, {}, { withCredentials: true });
                  toast.success(`Profil „${res.data.profile_label}" angewendet — ${res.data.subpages_queued} Unterseiten in Queue.`);
                  setIndustryProfile({ ...industryProfile, is_applied: true });
                  // Reload project to pick up new requested_subpages
                  try {
                    const p = await axios.get(`${API}/projects/${id}`, { withCredentials: true });
                    setProject(p.data);
                  } catch (_) {}
                } catch (e) { toast.error(e.response?.data?.detail || "Profil anwenden fehlgeschlagen"); }
              }}
            >
              Profil anwenden
            </Button>
          )}
        </div>
      )}

      {/* Scraped-Data Info-Card — DEPRECATED: ersetzt durch CompactProjectStatusStrip */}
      {false && project?.scraped_summary?.available && (
        <div
          className="rounded-sm border border-emerald-500/30 bg-emerald-50/60 dark:bg-emerald-500/5 px-4 py-3"
          data-testid="scraped-summary-card"
        >
          <div className="flex items-start gap-3">
            <Globe size={16} className="text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground">
                Aus alter Website übernommen
                {project.scraped_summary.source_url && (
                  <a
                    href={project.scraped_summary.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-2 text-xs font-normal text-emerald-700 dark:text-emerald-400 hover:underline inline-flex items-center gap-1"
                    data-testid="scraped-source-link"
                  >
                    {(() => { try { return new URL(project.scraped_summary.source_url).hostname.replace(/^www\./, ""); } catch (_) { return ""; } })()}
                    <ExternalLink size={10} />
                  </a>
                )}
              </p>
              <div className="flex flex-wrap gap-x-3 gap-y-1.5 mt-1.5 text-xs text-muted-foreground" data-testid="scraped-summary-chips">
                {project.scraped_summary.subpages_count > 0 && (
                  <span className="inline-flex items-center gap-1"><FileText size={11} />{project.scraped_summary.subpages_count} Subseiten gecrawlt</span>
                )}
                {project.scraped_summary.services_count > 0 && (
                  <span className="inline-flex items-center gap-1"><Sparkles size={11} />{project.scraped_summary.services_count} Leistungen</span>
                )}
                {project.scraped_summary.prices_count > 0 && (
                  <span className="inline-flex items-center gap-1"><Tag size={11} />{project.scraped_summary.prices_count} Preise</span>
                )}
                {project.scraped_summary.team_count > 0 && (
                  <span className="inline-flex items-center gap-1"><Users size={11} />{project.scraped_summary.team_count} Team-Mitglieder</span>
                )}
                {project.scraped_summary.faqs_count > 0 && (
                  <span className="inline-flex items-center gap-1"><HelpCircle size={11} />{project.scraped_summary.faqs_count} FAQs</span>
                )}
                {project.scraped_summary.testimonials_count > 0 && (
                  <span className="inline-flex items-center gap-1"><Quote size={11} />{project.scraped_summary.testimonials_count} Testimonials</span>
                )}
                {project.scraped_summary.gallery_count > 0 && (
                  <span className="inline-flex items-center gap-1"><ImageIcon size={11} />{project.scraped_summary.gallery_count} Galerie-Bilder</span>
                )}
                {project.scraped_summary.has_hours && (
                  <span className="inline-flex items-center gap-1"><Clock size={11} />Öffnungszeiten</span>
                )}
                {project.scraped_summary.has_logo && (
                  <span className="inline-flex items-center gap-1"><CheckCircle size={11} className="text-emerald-600" />Logo</span>
                )}
                {project.scraped_summary.social_count > 0 && (
                  <span className="inline-flex items-center gap-1"><Share2 size={11} />{project.scraped_summary.social_count} Social-Links</span>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mt-2 italic">
                Diese Daten fließen automatisch in die KI-Generierung. Eigene Wünsche kannst du im Feld <span className="font-medium text-foreground">„KI-Anweisungen"</span> ergänzen.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Requested Subpages Queue — DEPRECATED: ersetzt durch CompactProjectStatusStrip */}
      {false && project?.requested_subpages && project.requested_subpages.length > 0 && (
        <div
          className="rounded-sm border border-border bg-card px-4 py-2.5 flex items-center gap-2 flex-wrap"
          data-testid="subpages-queue-banner"
        >
          <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">Warteschlange:</span>
          {project.requested_subpages.slice(0, 8).map((sp, idx) => (
            <span
              key={idx}
              className="text-[11px] px-2 py-0.5 rounded-sm bg-muted text-foreground border border-border"
              data-testid={`queued-subpage-${idx}`}
              title={sp.brief || ""}
            >
              {sp.title}
            </span>
          ))}
          {project.requested_subpages.length > 8 && (
            <span className="text-[11px] text-muted-foreground">+{project.requested_subpages.length - 8} weitere</span>
          )}
          <span className="text-[10px] text-muted-foreground ml-auto italic">Werden beim nächsten „Website erstellen" mit-generiert</span>
        </div>
      )}

      {/* Main Content */}
      <div className={`flex-1 grid gap-4 min-h-0 transition-[grid-template-columns] duration-200 ${settingsOpen ? "grid-cols-[320px_1fr]" : "grid-cols-[44px_1fr]"}`}>
        {/* Left Panel — collapsible "Generieren-Modus" */}
        {settingsOpen ? (
        <div className="bg-card border border-border rounded-sm p-5 space-y-5 overflow-y-auto relative">
          <button
            type="button"
            onClick={toggleSettings}
            data-testid="settings-collapse-btn"
            title="Einstellungen einklappen — mehr Platz für Vorschau & Editor"
            className="absolute top-3 right-3 p-1 rounded-sm border border-border bg-card hover:bg-muted text-muted-foreground hover:text-foreground transition-colors z-10"
          >
            <ChevronLeft size={14} />
          </button>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3 border-b border-border pb-2">Einstellungen</p>

            {/* Firmeninfo */}
            <div className="space-y-2 mb-5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Firma</p>
              <div className="bg-muted/50 rounded-sm px-3 py-2.5 space-y-1">
                <p className="text-sm font-medium text-foreground">{company.name}</p>
                {company.industry && <p className="text-xs text-muted-foreground">{company.industry}</p>}
                {company.description && <p className="text-xs text-muted-foreground line-clamp-2">{company.description}</p>}
              </div>
            </div>

            {/* Template Selection */}
            <div className="space-y-2 mb-5">
              <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Template</Label>
              <Select value={templateId} onValueChange={setTemplateId}>
                <SelectTrigger className="rounded-sm" data-testid="builder-template-select">
                  <SelectValue placeholder="Template wählen" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: t.primary_color }} />
                        {t.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {template && <p className="text-xs text-muted-foreground">{template.description}</p>}
            </div>

            {/* AI Instructions */}
            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">KI-Anweisungen (optional)</Label>
              <Textarea
                value={instructions}
                onChange={e => setInstructions(e.target.value)}
                placeholder={project?.scraped_summary?.available
                  ? 'Eigene Wünsche an die KI – z.B. „Fokus auf Senioren-Therapie", „lockerer Ton", „Headline soll mehr Vertrauen ausstrahlen"…'
                  : 'z.B. Fokus auf Nachhaltigkeit, freundlicher Ton, 3 Dienstleistungen: Buchhaltung, Steuer, Beratung...'}
                className="rounded-sm text-sm"
                rows={5}
                data-testid="ai-instructions-input"
              />
              <p className="text-xs text-muted-foreground">
                {project?.scraped_summary?.available
                  ? "Die gescrapten Inhalte aus der alten Website laufen automatisch im Hintergrund mit – hier kannst du gezielt eigene Vorgaben ergänzen."
                  : "Die KI nutzt Firmendaten + Template, um passende Website-Texte zu erstellen."}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-2 pt-2 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowSubpagesModal(true)}
              className="w-full rounded-sm gap-2 text-sm border-violet-500/40 text-violet-700 dark:text-violet-300 hover:bg-violet-500/10"
              data-testid="open-subpage-suggestions-btn"
            >
              <ListChecks size={14} /> Subpages auswählen
              {(project?.requested_subpages?.length || 0) > 0 && (
                <span className="ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-sm bg-violet-500/20">
                  {project.requested_subpages.length} in Queue
                </span>
              )}
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={generating || !templateId}
              className="w-full rounded-sm gap-2"
              data-testid="generate-btn"
            >
              {generating ? (
                <><RefreshCw size={14} className="animate-spin" /> Website wird erstellt...</>
              ) : (
                <><Wand2 size={14} /> {previewUrl ? "Neu erstellen" : "Website erstellen"}</>
              )}
            </Button>
            {previewUrl && (
              <Button variant="outline" onClick={handleDownload} className="w-full rounded-sm gap-2 text-sm" data-testid="download-btn">
                <Download size={14} /> HTML herunterladen
              </Button>
            )}
          </div>
        </div>
        ) : (
        <div className="bg-card border border-border rounded-sm flex flex-col items-center py-3 gap-3" data-testid="settings-rail">
          <button
            type="button"
            onClick={toggleSettings}
            data-testid="settings-expand-btn"
            title="Einstellungen ausklappen (Template, KI-Anweisungen, Neu erstellen)"
            className="p-1.5 rounded-sm border border-border bg-card hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronRight size={14} />
          </button>
          <button
            type="button"
            onClick={() => { setSettingsOpen(true); try { localStorage.setItem("ws_settings_open", "1"); } catch {} }}
            title="Einstellungen öffnen"
            className="p-1.5 rounded-sm hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Einstellungen"
          >
            <Settings2 size={16} />
          </button>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating || !templateId}
            title={previewUrl ? "Demo neu erstellen" : "Website erstellen"}
            className="p-1.5 rounded-sm bg-foreground text-background hover:opacity-90 transition disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="generate-rail-btn"
            aria-label={previewUrl ? "Neu erstellen" : "Website erstellen"}
          >
            {generating ? <RefreshCw size={16} className="animate-spin" /> : <Wand2 size={16} />}
          </button>
          {previewUrl && (
            <button
              type="button"
              onClick={handleDownload}
              title="HTML herunterladen"
              className="p-1.5 rounded-sm hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              data-testid="download-rail-btn"
              aria-label="HTML herunterladen"
            >
              <Download size={16} />
            </button>
          )}
          <div className="flex-1" />
          <span className="text-[9px] uppercase tracking-widest text-muted-foreground rotate-180" style={{writingMode:"vertical-rl"}}>Bauen-Modus</span>
        </div>
        )}

        {/* Preview / Editor Panel */}
        <div className="bg-card border border-border rounded-sm overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/30 shrink-0">
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setViewMode("preview")}
                disabled={!previewUrl}
                data-testid="tab-preview-btn"
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${viewMode === "preview" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"} disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                <Eye size={12} /> Vorschau
              </button>
              <button
                type="button"
                onClick={openEditor}
                disabled={!(project?.generated_content?.hero || project?.content?.hero)}
                data-testid="tab-edit-btn"
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${viewMode === "edit" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"} disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                <FileEdit size={12} /> Inhalte bearbeiten
              </button>
              <button
                type="button"
                onClick={() => setViewMode("booking")}
                data-testid="tab-booking-btn"
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${viewMode === "booking" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
              >
                <CalendarIcon size={12} /> Buchung
              </button>
              <button
                type="button"
                onClick={() => setViewMode("inquiry")}
                data-testid="tab-inquiry-btn"
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${viewMode === "inquiry" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
              >
                <Sparkles size={12} /> Projekt-Anfragen
              </button>
            </div>
            {previewUrl && viewMode === "preview" && (
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => setShareOpen(true)} disabled={!previewUrl} className="flex items-center gap-1 text-xs text-foreground hover:text-primary transition-colors" data-testid="open-share-btn">
                  <Share2 size={11} /> {project?.public_enabled ? "Freigabe-Link" : "Für Kunden freigeben"}
                </button>
                <a href={previewUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 transition-colors" data-testid="preview-external-link">
                  <ExternalLink size={11} /> Öffnen
                </a>
              </div>
            )}
            {viewMode === "edit" && editContent && (
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={handleSuggestContent} disabled={suggestingContent || savingContent} className="rounded-sm text-xs h-7 px-3 gap-1.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white border-0 hover:opacity-90" data-testid="suggest-content-btn">
                  {suggestingContent ? <><RefreshCw size={11} className="animate-spin" /> KI denkt nach...</> : <><Sparkles size={11} /> KI-Vorschlag einfügen</>}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setViewMode("preview")} className="rounded-sm text-xs h-7 px-3" data-testid="cancel-edit-btn">
                  Abbrechen
                </Button>
                <Button size="sm" onClick={handleSaveContent} disabled={savingContent} className="rounded-sm text-xs h-7 px-3 gap-1.5" data-testid="save-content-btn">
                  {savingContent ? <><RefreshCw size={11} className="animate-spin" /> Speichert...</> : <><Save size={11} /> Speichern & Vorschau</>}
                </Button>
              </div>
            )}
          </div>

          {/* Page Selector Toolbar – Preview-Modus */}
          {viewMode === "preview" && previewUrl && (
            <div className="flex items-center gap-1 px-4 py-1.5 border-b border-border bg-card shrink-0 overflow-x-auto" data-testid="page-selector-toolbar">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-2 shrink-0">Seite:</span>
              {availablePages.map(p => {
                const fixed = { index: "Startseite", impressum: "Impressum", datenschutz: "Datenschutz" };
                const custom = customPagesMeta.find(cp => cp.slug === p);
                const label = fixed[p] || custom?.title || p;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => handlePageSwitch(p)}
                    data-testid={`page-btn-${p}`}
                    className={`text-xs px-2.5 py-1 rounded-sm transition-colors shrink-0 ${currentPage === p ? "bg-foreground text-background font-semibold" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
                  >
                    {label}
                  </button>
                );
              })}
              <div className="flex-1" />
              {/* Inline-Edit-Toggle (nur Premium-2026 Templates) */}
              {["aurora-glass", "neo-brutalist", "liquid-chrome"].includes(templateId) && (
                <button
                  type="button"
                  onClick={() => {
                    const next = !inlineEditMode;
                    setInlineEditMode(next);
                    toast.success(next
                      ? "Inline-Bearbeitung aktiv — klick direkt auf Texte in der Vorschau"
                      : "Inline-Bearbeitung deaktiviert");
                  }}
                  title={inlineEditMode
                    ? "Inline-Bearbeitung deaktivieren"
                    : "Texte direkt in der Vorschau klicken & bearbeiten"}
                  className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-sm border transition-all shrink-0 mr-2 ${
                    inlineEditMode
                      ? "bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white border-violet-600 shadow-sm"
                      : "bg-card text-muted-foreground border-border hover:text-violet-600 hover:border-violet-400"
                  }`}
                  data-testid="toggle-inline-edit-preview"
                >
                  <Edit3 size={11} /> {inlineEditMode ? "Inline an" : "Inline-Edit"}
                </button>
              )}
              {/* Device-Toggle (Mobile/Tablet/Desktop) */}
              <div className="flex bg-muted rounded-sm p-0.5 mr-2 shrink-0" role="tablist" aria-label="Geräte-Vorschau">
                {[
                  { key: "desktop", Icon: Monitor, label: "Desktop" },
                  { key: "tablet",  Icon: Tablet,  label: "Tablet" },
                  { key: "mobile",  Icon: Smartphone, label: "Mobile" },
                ].map(({ key, Icon, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setPreviewDevicePersist(key)}
                    title={label}
                    aria-label={label}
                    data-testid={`preview-device-${key}`}
                    className={`flex items-center justify-center w-7 h-6 rounded-sm transition-colors ${previewDevice === key ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  >
                    <Icon size={12} />
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={openAiCreateModal}
                disabled={generating}
                data-testid="ai-create-page-toolbar-btn"
                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-sm bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700 transition-all shadow-sm shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Sparkles size={11} /> Seite mit KI erstellen
              </button>
            </div>
          )}

          {!previewUrl && !generating && viewMode === "preview" && (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
              <div className="w-16 h-16 rounded-sm bg-muted flex items-center justify-center mb-4">
                <Wand2 size={24} className="text-muted-foreground" />
              </div>
              <p className="text-sm font-medium text-foreground mb-1">Noch keine Vorschau</p>
              <p className="text-xs text-muted-foreground max-w-xs">Wähle ein Template, füge optional Anweisungen hinzu und klicke auf "Website generieren".</p>
            </div>
          )}

          {generating && (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8" data-testid="generating-panel">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-5">
                <Wand2 size={24} className="text-primary animate-pulse" />
              </div>
              <p className="text-base font-semibold text-foreground mb-2">Deine Website wird erstellt…</p>
              <p className="text-xs text-muted-foreground mb-6">Texte, Bilder, Unterseiten und Qualitätsprüfung — das dauert ca. 1–3 Minuten. Du kannst den Tab offen lassen.</p>

              {/* Progress bar */}
              <div className="w-full max-w-md">
                <div className="h-2 bg-muted rounded-full overflow-hidden" data-testid="gen-progress-bar">
                  <div
                    className="h-full bg-primary transition-all duration-500 ease-out"
                    style={{ width: `${Math.min(100, Math.max(5, genProgress))}%` }}
                  />
                </div>
                <div className="flex items-center justify-between mt-2 text-[11px] text-muted-foreground">
                  <span data-testid="gen-progress-label">{genProgress || 5}%</span>
                  <span className="font-medium text-foreground" data-testid="gen-phase-label">{({
                    queued: "Wird gestartet…",
                    texte: "KI schreibt Texte…",
                    "hero-bild": "Hero-Bild wird erstellt…",
                    "stock-bilder": "Stock-Bilder werden geholt…",
                    unterseiten: "Unterseiten werden generiert…",
                    "html-zusammensetzen": "HTML wird zusammengebaut…",
                    "design-konzept": "KI-Art-Director komponiert das Design…",
                    qualitaetspruefung: "KI prüft die Seite (Screenshot-Analyse)…",
                    fertig: "Fertig!",
                    fehler: "Fehler",
                  }[genPhase]) || "Läuft…"}</span>
                </div>
              </div>

              {/* Step indicators */}
              <div className="flex items-center gap-1.5 mt-6">
                {["queued","texte","hero-bild","stock-bilder","unterseiten","html-zusammensetzen","qualitaetspruefung","fertig"].map((p, i) => {
                  const order = { queued:0, texte:1, "hero-bild":2, "stock-bilder":3, unterseiten:4, "html-zusammensetzen":5, qualitaetspruefung:6, fertig:7 };
                  const cur = order[genPhase] ?? 0;
                  const isDone = i < cur;
                  const isActive = i === cur;
                  return (
                    <div
                      key={p}
                      className={`h-1.5 rounded-full transition-all duration-500 ${
                        isDone ? "w-4 bg-primary" : isActive ? "w-6 bg-primary animate-pulse" : "w-4 bg-muted"
                      }`}
                      data-testid={`gen-step-${p}`}
                    />
                  );
                })}
              </div>

              {/* Cancel button */}
              <button
                type="button"
                onClick={handleCancelGenerate}
                disabled={cancelling}
                className="mt-8 inline-flex items-center gap-2 px-4 py-2 text-xs font-medium text-muted-foreground hover:text-destructive border border-border hover:border-destructive/40 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="cancel-generate-btn"
              >
                <X size={14} />
                {cancelling ? "Wird abgebrochen…" : "Generierung abbrechen"}
              </button>
            </div>
          )}

          {previewUrl && !generating && viewMode === "preview" && (
            <div
              className={`flex-1 w-full overflow-auto flex justify-center ${previewDevice !== "desktop" ? "bg-muted/40 py-6 px-4" : ""}`}
              data-testid="preview-frame-wrapper"
            >
              <div
                className={`relative bg-card transition-all ${
                  previewDevice === "desktop"
                    ? "w-full h-full"
                    : previewDevice === "tablet"
                    ? "w-[820px] max-w-full h-[1100px] max-h-full rounded-2xl shadow-2xl border-[10px] border-foreground/85 ring-1 ring-foreground/20 overflow-hidden"
                    : "w-[390px] max-w-full h-[820px] max-h-full rounded-[2.4rem] shadow-2xl border-[12px] border-foreground/90 ring-1 ring-foreground/20 overflow-hidden"
                }`}
                data-testid={`preview-device-frame-${previewDevice}`}
              >
                <iframe
                  src={previewUrl}
                  className={`w-full h-full border-0 ${previewDevice === "desktop" ? "" : "block"}`}
                  title="Website Vorschau"
                  data-testid="preview-iframe"
                  sandbox="allow-scripts allow-same-origin allow-popups allow-top-navigation-by-user-activation"
                />
              </div>
            </div>
          )}

          {viewMode === "booking" && (
            <div className="flex-1 overflow-y-auto p-6" data-testid="booking-container">
              <div className="max-w-3xl mx-auto">
                <div className="mb-6">
                  <h2 className="text-xl font-bold tracking-tight">Online-Terminbuchung</h2>
                  <p className="text-sm text-muted-foreground mt-1">Definiere Leistungen + Öffnungszeiten. Sobald aktiviert erscheint auf der Website ein Buchungs-Widget mit echter Zeitslot-Auswahl.</p>
                </div>
                <BookingEditor projectId={id} companyId={project?.company_id} />
              </div>
            </div>
          )}

          {viewMode === "inquiry" && (
            <div className="flex-1 overflow-y-auto" data-testid="inquiry-container">
              <ProjectInquiryEditor projectId={id} />
            </div>
          )}

          {viewMode === "edit" && editContent && (
            <div className={`flex-1 min-h-0 grid ${splitView && previewUrl ? "grid-cols-[1fr_1fr]" : "grid-cols-1"}`}>
            <div ref={editorScrollRef} className={`overflow-y-auto bg-muted/20 transition-all ${splitView && previewUrl ? "" : MP_PADDING}`} data-testid="content-editor">
              <div className="max-w-5xl mx-auto px-6 py-6 flex gap-5">
                <aside className="hidden lg:block sticky top-4 self-start w-48 shrink-0" data-testid="page-nav">
                  <div className="bg-card border border-border rounded-lg p-2 space-y-1">
                    <div className="px-2 pt-1.5 pb-1 text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Allgemein</div>
                    <button
                      type="button"
                      onClick={() => setCurrentEditPage("globals")}
                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-xs rounded-md transition-all ${currentEditPage === "globals" ? "bg-foreground text-background font-semibold" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}
                      data-testid="page-tab-globals"
                      title="Theme, Logo & Bilder — gilt für alle Seiten"
                    >
                      <Settings2 size={12} /> Globales
                    </button>
                    <div className="px-2 pt-2 pb-1 text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Seiten</div>
                    <button
                      type="button"
                      onClick={() => setCurrentEditPage("index")}
                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-xs rounded-md transition-all ${currentEditPage === "index" ? "bg-foreground text-background font-semibold" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}
                      data-testid="page-tab-index"
                    >
                      <Globe size={12} /> Startseite
                    </button>
                    {(editContent.custom_pages || []).map((cp, i) => (
                      <button
                        key={cp.slug || i}
                        type="button"
                        onClick={() => setCurrentEditPage(`custom:${cp.slug}`)}
                        className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-xs rounded-md transition-all truncate ${currentEditPage === `custom:${cp.slug}` ? "bg-foreground text-background font-semibold" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}
                        data-testid={`page-tab-custom-${cp.slug || i}`}
                        title={cp.title || cp.slug}
                      >
                        <FileText size={12} className="shrink-0" />
                        <span className="truncate">{cp.title || cp.slug || `Seite ${i + 1}`}</span>
                      </button>
                    ))}
                    <div className="px-2 pt-2 pb-1 text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Rechtliches</div>
                    <button
                      type="button"
                      onClick={() => setCurrentEditPage("impressum")}
                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-xs rounded-md transition-all ${currentEditPage === "impressum" ? "bg-foreground text-background font-semibold" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}
                      data-testid="page-tab-impressum"
                    >
                      <FileEdit size={12} /> Impressum
                    </button>
                    <button
                      type="button"
                      onClick={() => setCurrentEditPage("datenschutz")}
                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-xs rounded-md transition-all ${currentEditPage === "datenschutz" ? "bg-foreground text-background font-semibold" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}
                      data-testid="page-tab-datenschutz"
                    >
                      <FileEdit size={12} /> Datenschutz
                    </button>
                    <div className="h-px bg-border my-2" />
                    <button
                      type="button"
                      onClick={() => setShowNewPageModal(true)}
                      className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs rounded-md border border-dashed border-border text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-all"
                      data-testid="page-tab-new"
                    >
                      <Plus size={12} /> Neue Seite
                    </button>
                  </div>
                </aside>
                <div className="flex-1 min-w-0 space-y-4">
                {/* Mode-Toggle: Schnell vs. Alle Optionen */}
                <div className="bg-card border border-border rounded-lg p-3 flex items-center gap-3">
                  <div className="flex-1">
                    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Bearbeitungs-Modus</div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {editorMode === "quick"
                        ? "Nur die wichtigsten Felder für eine schnelle Demo-Anpassung."
                        : "Alle Optionen sichtbar — inkl. Theme, Hero-Stil, Stats, Custom-Pages."}
                    </p>
                  </div>
                  {previewUrl && (
                    <button
                      type="button"
                      onClick={toggleSplitView}
                      title="Split-View: Editor + Live-Vorschau Seite an Seite"
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border transition-all shrink-0 ${
                        splitView
                          ? "bg-foreground text-background border-foreground"
                          : "bg-card text-muted-foreground border-border hover:text-foreground hover:border-foreground/40"
                      }`}
                      data-testid="toggle-split-view"
                    >
                      <Layers size={12} /> {splitView ? "Split an" : "Split"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={toggleMiniPreview}
                    disabled={splitView && !!previewUrl}
                    title={splitView ? "Floating-Vorschau im Split-Modus deaktiviert" : "Floating Live-Vorschau ein/ausblenden"}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border transition-all shrink-0 ${
                      miniPreviewOpen && !splitView
                        ? "bg-foreground text-background border-foreground"
                        : "bg-card text-muted-foreground border-border hover:text-foreground hover:border-foreground/40"
                    } disabled:opacity-40 disabled:cursor-not-allowed`}
                    data-testid="toggle-mini-preview"
                  >
                    <Eye size={12} /> {miniPreviewOpen && !splitView ? "Live an" : "Floating"}
                  </button>
                  {["aurora-glass", "neo-brutalist", "liquid-chrome"].includes(templateId) && (
                    <button
                      type="button"
                      onClick={() => {
                        const next = !inlineEditMode;
                        setInlineEditMode(next);
                        toast.success(next
                          ? "Inline-Bearbeitung aktiv — klick direkt auf Texte in der Vorschau"
                          : "Inline-Bearbeitung deaktiviert");
                      }}
                      title={inlineEditMode
                        ? "Inline-Bearbeitung deaktivieren"
                        : "Texte direkt in der Vorschau klicken & bearbeiten"}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border transition-all shrink-0 ${
                        inlineEditMode
                          ? "bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white border-violet-600 shadow-sm"
                          : "bg-card text-muted-foreground border-border hover:text-violet-600 hover:border-violet-400"
                      }`}
                      data-testid="toggle-inline-edit"
                    >
                      <Edit3 size={12} /> {inlineEditMode ? "Inline an" : "Inline"}
                    </button>
                  )}
                  <div className="flex bg-muted rounded-md p-0.5 shrink-0" role="tablist" aria-label="Editor-Modus">
                    <button
                      type="button"
                      onClick={() => setEditorModePersist("quick")}
                      data-testid="editor-mode-quick"
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded transition-colors ${editorMode === "quick" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                    >
                      <Zap size={12} /> Schnell
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditorModePersist("pro")}
                      data-testid="editor-mode-pro"
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded transition-colors ${editorMode === "pro" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                    >
                      <Settings2 size={12} /> Alle Optionen
                    </button>
                  </div>
                </div>

                {/* THEME (Farben & Stil) — nur Pro-Modus */}
                {editorMode === "pro" && showSection("theme") && (
                  <EditorSection
                    icon={Palette}
                    title="Farben & Stil"
                    subtitle="Theme-Preset, Akzentfarbe, Hero-Variante"
                    defaultOpen={false}
                    testid="theme"
                  >
                    <ThemeEditor
                      templateId={templateId}
                      theme={editContent.theme}
                      setTheme={(v) => setEditContent(prev => ({ ...prev, theme: v }))}
                      presetKey={editContent.theme_preset}
                      setPresetKey={(v) => setEditContent(prev => ({ ...prev, theme_preset: v }))}
                      heroVariant={editContent.hero_variant}
                      setHeroVariant={(v) => setEditContent(prev => ({ ...prev, hero_variant: v }))}
                      logoUrl={editContent.logo_url}
                      projectId={id}
                    />
                  </EditorSection>
                )}

                {/* HAUPTSEKTIONEN AKTIVIEREN — nur Index-Page-Tab, schöner Master-Switch-Block */}
                {currentEditPage === "index" && (
                  <div className="bg-card border border-border rounded-lg p-4" data-testid="section-master-toggles">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Hauptsektionen aktivieren</div>
                        <p className="text-[11px] text-muted-foreground mt-0.5">Schalte Bereiche der Startseite an/aus — auch solche, die andere Demos haben aber deine nicht.</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { key: "stats", label: "Vertrauenszahlen", icon: BarChart3, desc: "4 Kacheln mit Zahlen", autoFill: true },
                        { key: "about", label: "Über uns", icon: BookText, desc: "Wer ihr seid", autoFill: true },
                        { key: "services", label: "Leistungen", icon: Briefcase, desc: "Was ihr anbietet", autoFill: true },
                        { key: "team_members", label: "Team", icon: Users, desc: "Mitarbeiter-Vorstellung", movedToSection: "team-members" },
                        { key: "testimonials", label: "Kundenstimmen", icon: Quote, desc: "Reviews & Testimonials", autoFill: true, movedToSection: "testimonials" },
                        { key: "pricing", label: "Pakete & Preise", icon: Tag, desc: "Preistabelle", autoFill: true },
                        { key: "social_links", label: "Social Media", icon: Share2, desc: "Footer-Links" },
                        { key: "contact", label: "Kontakt", icon: Mail, desc: "Kontaktformular" },
                      ].filter(s => {
                        // Wenn eine Subpage den Data-Source schon beansprucht,
                        // diesen Master-Toggle auf der Startseite ausblenden —
                        // der User bearbeitet es direkt auf der Subpage.
                        if (!s.movedToSection) return true;
                        return !customPageMatchesSection(s.movedToSection);
                      }).map(s => {
                        const active = isSectionEnabled(s.key);
                        const Icon = s.icon;
                        return (
                          <button
                            key={s.key}
                            type="button"
                            onClick={async () => {
                              const newActive = !active;
                              setSectionEnabled(s.key, newActive);
                              // Wenn aktiviert UND leer → Auto-Fill via KI
                              if (newActive && s.autoFill) {
                                await autoFillSection(s.key);
                              }
                            }}
                            className={`flex items-start gap-2 p-2.5 rounded-md border transition-all text-left ${active ? "border-foreground/40 bg-muted/30" : "border-dashed border-border bg-card hover:border-foreground/20 opacity-60"}`}
                            data-testid={`master-toggle-${s.key}`}
                          >
                            <div className={`mt-0.5 p-1 rounded-sm ${active ? "bg-foreground text-background" : "bg-muted text-muted-foreground"}`}>
                              <Icon size={11} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-semibold">{s.label}</span>
                                {active && <CheckCircle size={10} className="text-emerald-600 shrink-0" />}
                                {s.autoFill && <Sparkles size={9} className="text-violet-500 shrink-0" />}
                              </div>
                              <div className="text-[10px] text-muted-foreground">
                                {active ? s.desc : (s.autoFill ? "Klick — KI füllt es passend zur Marke" : "Klick zum Aktivieren")}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* LOGO & BILDER — Quick-Modus */}
                {showSection("media") && (
                <EditorSection
                  icon={ImageIcon}
                  title="Logo & Bilder"
                  subtitle="Firmen-Logo, Hero-Bild, Über-uns-Bild"
                  quick
                  testid="media"
                >
                  <div data-testid="media-editor" className="space-y-3">
                    <div className="space-y-1">
                      <MediaUploader
                        projectId={id}
                        slot="logo"
                        currentUrl={editContent.logo_url}
                        onUploaded={(url) => { setEditContent(prev => ({ ...prev, logo_url: url })); refreshPreview(); }}
                        onDeleted={() => { setEditContent(prev => ({ ...prev, logo_url: "" })); refreshPreview(); }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowLogoPicker(true)}
                        className="w-full flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-semibold text-violet-700 bg-violet-500/10 hover:bg-violet-500/20 rounded-sm transition-colors"
                        data-testid="open-logo-suggestions"
                      >
                        <Dice5 size={11} /> Logo aus Initialen generieren (6 Varianten)
                      </button>
                    </div>
                    <MediaUploader
                      projectId={id}
                      slot="hero"
                      currentUrl={editContent.images?.hero || ""}
                      onUploaded={(url) => { setEditContent(prev => ({ ...prev, images: { ...(prev.images || {}), hero: url } })); refreshPreview(); }}
                      onDeleted={() => { setEditContent(prev => { const img = { ...(prev.images || {}) }; delete img.hero; return { ...prev, images: img }; }); refreshPreview(); }}
                    />
                    <MediaUploader
                      projectId={id}
                      slot="about"
                      currentUrl={editContent.images?.about || ""}
                      rerollSlot="about"
                      onUploaded={(url) => { setEditContent(prev => ({ ...prev, images: { ...(prev.images || {}), about: url } })); refreshPreview(); }}
                      onDeleted={() => { setEditContent(prev => { const img = { ...(prev.images || {}) }; delete img.about; return { ...prev, images: img }; }); refreshPreview(); }}
                    />
                    {/* Service-Bilder — auch im Quick-Mode sichtbar (max 3 Bilder, weil Templates nur 3 rendern) */}
                    {(editContent.images?.services || []).length > 0 && (
                      <div className="pt-3 border-t border-border">
                        <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Bilder bei „Leistungen"</Label>
                        <p className="text-[10px] text-muted-foreground mb-2">Klick auf 🎲 lädt ein neues Bild aus dem Branchen-Pool</p>
                        <div className="grid grid-cols-3 gap-2">
                          {(editContent.images.services).slice(0, 3).map((url, i) => (
                            <ServiceImageTile key={i} projectId={id} index={i} url={url} onChanged={(newUrl) => {
                              setEditContent(prev => {
                                const services = [...(prev.images?.services || [])];
                                services[i] = newUrl;
                                return { ...prev, images: { ...(prev.images || {}), services } };
                              });
                              refreshPreview();
                            }} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </EditorSection>
                )}

                {/* NAVIGATION — Stil, Farben, CTA, Logo, Link-Reihenfolge */}
                {showSection("nav") && (
                <EditorSection
                  icon={Navigation}
                  title="Navigation (Menüleiste)"
                  subtitle="Farben, Stil, CTA-Button, Logo-Text, Link-Reihenfolge"
                  quick
                  testid="nav"
                >
                  <NavEditor
                    value={editContent.nav}
                    onChange={(next) => setEditContent(prev => ({ ...prev, nav: next }))}
                    customPages={editContent.custom_pages || []}
                    companyPhone={company?.phone || editContent.contact?.phone || ""}
                    companyEmail={company?.email || editContent.contact?.email || ""}
                  />
                </EditorSection>
                )}

                {/* HERO — Quick-Modus */}
                {showSection("hero") && (
                <EditorSection
                  icon={Type}
                  title="Hauptbereich (oben auf der Website)"
                  subtitle="Schlagzeile, Beschreibung, Button-Text"
                  quick
                  testid="hero"
                >
                  <div>
                    <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Eyebrow (kleiner Tag über der Headline)</Label>
                    <Input
                      value={editContent.hero.eyebrow || ""}
                      onChange={e => updateField("hero", "eyebrow", e.target.value)}
                      placeholder={`Standard: „${(company?.industry || "Ihr Partner").toUpperCase()}"`}
                      className="rounded-md mt-1"
                      data-testid="edit-hero-eyebrow"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1 italic">Optional — leer = Branche aus Stammdaten wird verwendet.</p>
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Schlagzeile</Label>
                    <Input value={editContent.hero.title} onChange={e => updateField("hero", "title", e.target.value)} maxLength={80} className="rounded-md mt-1" data-testid="edit-hero-title" />
                    <div className="flex justify-end">
                      <span className={`text-[10px] mt-0.5 ${(editContent.hero.title || "").length > 65 ? "text-amber-700" : "text-muted-foreground"}`} data-testid="hero-title-counter">
                        {(editContent.hero.title || "").length}/80
                      </span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Beschreibung (1-2 Sätze)</Label>
                    <Textarea
                      value={editContent.hero.subtitle}
                      onChange={e => updateField("hero", "subtitle", e.target.value)}
                      rows={2}
                      maxLength={240}
                      className="rounded-md mt-1 text-sm"
                      data-testid="edit-hero-subtitle"
                    />
                    <div className="flex justify-end">
                      <span className={`text-[10px] mt-0.5 ${(editContent.hero.subtitle || "").length > 200 ? "text-amber-700" : "text-muted-foreground"}`} data-testid="hero-subtitle-counter">
                        {(editContent.hero.subtitle || "").length}/240 — empfohlen ≤200
                      </span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Button-Text</Label>
                    <Input value={editContent.hero.cta} onChange={e => updateField("hero", "cta", e.target.value)} placeholder="z.B. Jetzt anfragen" className="rounded-md mt-1" data-testid="edit-hero-cta" />
                  </div>
                  <div className="pt-2 border-t border-border/40">
                    <CtaActionEditor
                      value={editContent.hero.cta_action}
                      onChange={(next) => updateField("hero", "cta_action", next)}
                      customPages={editContent.custom_pages || []}
                      companyPhone={company?.phone || editContent.contact?.phone || ""}
                      companyEmail={company?.email || editContent.contact?.email || ""}
                      testidPrefix="edit-hero-cta-action"
                    />
                  </div>

                  {/* Hero-Stil-Auswahl — nur Pro-Modus */}
                  {editorMode === "pro" && (
                    <div className="pt-3 mt-3 border-t border-border space-y-3">
                      <Label className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                        <Sparkles size={11} /> Hero-Inhalt (Bild / Video / Animation)
                      </Label>
                      <p className="text-[10px] text-muted-foreground -mt-1 italic">Layout (zentriert/split/minimal) wird oben in „Farben & Stil" gesteuert.</p>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { v: "image", label: "Bild", desc: "Standard" },
                          { v: "video", label: "Video", desc: "MP4-Loop" },
                          { v: "particles", label: "Partikel", desc: "Animiert" },
                        ].map(opt => (
                          <button
                            key={opt.v}
                            type="button"
                            onClick={() => updateField("hero", "variant", opt.v)}
                            data-testid={`hero-variant-${opt.v}`}
                            className={`px-3 py-3 rounded-md border text-left transition ${
                              (editContent.hero.variant || "image") === opt.v
                                ? "border-primary bg-primary/5 ring-1 ring-primary"
                                : "border-border hover:border-muted-foreground/40"
                            }`}
                          >
                            <div className="text-xs font-semibold">{opt.label}</div>
                            <div className="text-[10px] text-muted-foreground mt-0.5">{opt.desc}</div>
                          </button>
                        ))}
                      </div>
                      {(editContent.hero.variant || "image") === "video" && (
                        <div className="space-y-1">
                          <Label className="text-xs">Video-URL (MP4, möglichst &lt; 5 MB, 10–20 s Loop)</Label>
                          <div className="flex gap-2">
                            <Input
                              value={editContent.hero.video_url || ""}
                              onChange={e => updateField("hero", "video_url", e.target.value)}
                              placeholder="https://example.com/hero-loop.mp4"
                              className="rounded-md mt-1 text-xs flex-1"
                              data-testid="edit-hero-video-url"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setShowVideoPicker(true)}
                              className="mt-1 gap-1.5 shrink-0"
                              data-testid="open-video-picker-btn"
                            >
                              <Sparkles size={12} /> Aus Library
                            </Button>
                          </div>
                          <p className="text-[10px] text-muted-foreground">Klick „Aus Library" für kuratierte Loop-Videos (gratis, branchenpassend).</p>
                        </div>
                      )}
                      {(editContent.hero.variant || "image") === "particles" && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Partikel-Farbe</Label>
                            <div className="flex gap-2 mt-1">
                              <input
                                type="color"
                                value={editContent.hero.particles_color || "#ffd84d"}
                                onChange={e => updateField("hero", "particles_color", e.target.value)}
                                className="h-9 w-12 rounded border cursor-pointer"
                                data-testid="edit-particles-color"
                              />
                              <Input
                                value={editContent.hero.particles_color || "#ffd84d"}
                                onChange={e => updateField("hero", "particles_color", e.target.value)}
                                className="rounded-md text-xs flex-1"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs">Dichte ({editContent.hero.particles_density || 80})</Label>
                            <input
                              type="range"
                              min="20"
                              max="200"
                              step="5"
                              value={editContent.hero.particles_density || 80}
                              onChange={e => updateField("hero", "particles_density", +e.target.value)}
                              className="w-full mt-2 accent-primary"
                              data-testid="edit-particles-density"
                            />
                          </div>
                          <p className="col-span-2 text-[10px] text-muted-foreground">Mauscursor erzeugt Verbindungslinien zwischen Partikeln. Auf dunklem Hintergrund am effektvollsten.</p>
                        </div>
                      )}
                    </div>
                  )}
                </EditorSection>
                )}

                {/* STATS — nur Pro-Modus */}
                {editorMode === "pro" && showSection("stats") && (
                  <EditorSection
                    icon={BarChart3}
                    title="Vertrauenszahlen"
                    subtitle={'4 kleine Kacheln unter dem Hero (z.B. „10+ Jahre Erfahrung")'}
                    defaultOpen={false}
                    testid="stats"
                    enabled={isSectionEnabled("stats")}
                    onToggleEnabled={(v) => setSectionEnabled("stats", v)}
                  >
                    {/* Halluzinations-Warnung: wenn Werte wie "1,8★ auf Google" oder "19% Bestehensquote" drin sind */}
                    {editContent.stats.some(s => /\d[,.]\d\s*★|★\s*auf|bestehensquote/i.test(`${s.value} ${s.label}`)) && (
                      <div className="mb-2 p-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-md text-[11px] text-amber-900 dark:text-amber-200" data-testid="stats-hallucination-warning">
                        ⚠️ Diese Zahlen wirken wie KI-Halluzinationen (Google-Rating, Bestehensquote). Bitte prüfen oder entfernen — nichts Falsches an echte Fahrschüler senden.
                      </div>
                    )}
                    <SortableList
                      items={editContent.stats.map((_, i) => i)}
                      onReorder={(newOrder) => setEditContent(prev => ({ ...prev, stats: newOrder.map(i => prev.stats[i]) }))}
                      itemKey="stat"
                      renderItem={(idx, { dragHandleProps }) => {
                        const s = editContent.stats[idx];
                        return (
                          <div className="p-3 bg-muted/30 rounded-md space-y-2 border border-border" data-testid={`stat-card-${idx}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <button {...dragHandleProps}><DragHandleIcon /></button>
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Kachel {idx + 1}</span>
                              </div>
                              <button type="button" onClick={() => setEditContent(prev => ({ ...prev, stats: prev.stats.filter((_, i2) => i2 !== idx) }))} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`remove-stat-${idx}`}>
                                <Trash2 size={12} />
                              </button>
                            </div>
                            <Input value={s.value} onChange={e => updateStat(idx, "value", e.target.value)} placeholder="z.B. 10+ oder 98%" className="rounded-md text-sm" data-testid={`edit-stat-value-${idx}`} />
                            <Input value={s.label} onChange={e => updateStat(idx, "label", e.target.value)} placeholder="Beschriftung" className="rounded-md text-sm" data-testid={`edit-stat-label-${idx}`} />
                          </div>
                        );
                      }}
                    />
                    {editContent.stats.length < 4 && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => setEditContent(prev => ({ ...prev, stats: [...prev.stats, { value: "", label: "" }] }))} className="h-7 px-2 text-xs rounded-md gap-1 mt-2" data-testid="add-stat-btn">
                        <Plus size={11} /> Kachel hinzufügen
                      </Button>
                    )}
                  </EditorSection>
                )}

                {/* ABOUT — Quick-Modus */}
                {showSection("about") && (
                <EditorSection
                  icon={BookText}
                  title="Über uns"
                  subtitle="Wer ihr seid und was euch besonders macht"
                  quick
                  testid="about"
                  enabled={isSectionEnabled("about")}
                  onToggleEnabled={(v) => setSectionEnabled("about", v)}
                  headerAction={
                    <SectionRegenButton
                      projectId={id}
                      section="about"
                      onRegenerated={(data) => setEditContent(prev => ({ ...prev, about: { ...prev.about, ...data } }))}
                    />
                  }
                >
                  <div>
                    <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Überschrift</Label>
                    <Input value={editContent.about.heading} onChange={e => updateField("about", "heading", e.target.value)} className="rounded-md mt-1" data-testid="edit-about-heading" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Text</Label>
                    <Textarea value={editContent.about.text} onChange={e => updateField("about", "text", e.target.value)} rows={5} className="rounded-md mt-1 text-sm" data-testid="edit-about-text" />
                  </div>
                </EditorSection>
                )}

                {/* SERVICES — Quick-Modus */}
                {showSection("services") && (
                <EditorSection
                  icon={Briefcase}
                  title="Leistungen"
                  subtitle={`${editContent.services.length} Einträge${editContent.services.length < 6 ? " — bis zu 6 möglich" : " (Maximum)"}`}
                  quick
                  testid="services"
                  enabled={isSectionEnabled("services")}
                  onToggleEnabled={(v) => setSectionEnabled("services", v)}
                  headerAction={
                    <SectionRegenButton
                      projectId={id}
                      section="services"
                      onRegenerated={(data) => {
                        if (Array.isArray(data) && data.length) {
                          setEditContent(prev => ({
                            ...prev,
                            services: data.slice(0, 6).map(s => ({ title: s?.title || "", description: s?.description || "" })),
                          }));
                        }
                      }}
                    />
                  }
                >
                  <div className="flex justify-end -mt-1 mb-1">
                    {editContent.services.length < 6 && (
                      <Button type="button" size="sm" variant="ghost" onClick={addService} className="h-7 px-2 text-xs rounded-md gap-1" data-testid="add-service-btn">
                        <Plus size={11} /> Hinzufügen
                      </Button>
                    )}
                  </div>
                  {editContent.services.length === 0 && (
                    <p className="text-xs text-muted-foreground italic py-3 text-center">Noch keine Leistungen. Klicke auf „Hinzufügen".</p>
                  )}
                  <SortableList
                    items={editContent.services.map((_, i) => i)}
                    onReorder={(newOrder) => setEditContent(prev => ({ ...prev, services: newOrder.map(i => prev.services[i]) }))}
                    itemKey="service"
                    renderItem={(idx, { dragHandleProps }) => {
                      const s = editContent.services[idx];
                      return (
                        <div className="p-3 bg-muted/30 rounded-md space-y-2 border border-border" data-testid={`service-card-${idx}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <button {...dragHandleProps}><DragHandleIcon /></button>
                              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Leistung {idx + 1}</span>
                            </div>
                            <button type="button" onClick={() => removeService(idx)} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`remove-service-${idx}`}>
                              <Trash2 size={12} />
                            </button>
                          </div>
                          <Input value={s.title} onChange={e => updateService(idx, "title", e.target.value)} placeholder="Titel" className="rounded-md text-sm" data-testid={`edit-service-title-${idx}`} />
                          <Textarea value={s.description} onChange={e => updateService(idx, "description", e.target.value)} placeholder="Beschreibung" rows={2} className="rounded-md text-sm" data-testid={`edit-service-description-${idx}`} />
                        </div>
                      );
                    }}
                  />
                </EditorSection>
                )}

                {/* INDUSTRY-SPECIFIC — nur wenn Template Industry-Editor unterstützt (kein Dead-Tab) */}
                {editorMode === "pro" && hasIndustryEditor(templateId) && showSection("industry") && (
                  <EditorSection
                    icon={Layers}
                    title="Branchen-Inhalte"
                    subtitle="Speisekarte, Preisliste, Öffnungszeiten — je nach Branche"
                    defaultOpen={false}
                    testid="industry"
                  >
                    <IndustryEditor templateId={templateId} editContent={editContent} setEditContent={setEditContent} startSection={1} />
                  </EditorSection>
                )}

                {/* SECTION-ORDER (Phase 2) — nur für Premium-2026-Templates sichtbar */}
                {["aurora-glass", "neo-brutalist", "liquid-chrome"].includes(templateId) && showSection("section-order") && (
                  <EditorSection
                    icon={Layers}
                    title="Sektionen-Reihenfolge"
                    subtitle="Ziehe Sektionen per Drag & Drop in die gewünschte Reihenfolge"
                    defaultOpen={false}
                    testid="section-order"
                  >
                    {(() => {
                      const REORDERABLE_LABELS = {
                        services: { label: "Leistungen", icon: Briefcase },
                        stats: { label: "Vertrauenszahlen", icon: BarChart3 },
                        pricing: { label: "Pakete & Preise", icon: Tag },
                        testimonials: { label: "Kundenstimmen", icon: Quote },
                      };
                      const DEFAULT = ["services", "stats", "pricing", "testimonials"];
                      const currentOrder = editContent.section_order && editContent.section_order.length > 0
                        ? [...editContent.section_order.filter(k => DEFAULT.includes(k)), ...DEFAULT.filter(k => !editContent.section_order.includes(k))]
                        : DEFAULT;
                      return (
                        <>
                          <p className="text-[11px] text-muted-foreground mb-2">
                            Hero und Kontakt bleiben immer oben/unten. Diese 4 Sektionen kannst du frei sortieren:
                          </p>
                          <SortableList
                            items={currentOrder}
                            onReorder={(newOrder) => setEditContent(prev => ({ ...prev, section_order: newOrder }))}
                            itemKey="section-order"
                            renderItem={(sectionKey, { dragHandleProps }) => {
                              const meta = REORDERABLE_LABELS[sectionKey];
                              if (!meta) return null;
                              const Icon = meta.icon;
                              const isOff = editContent.sections?.[sectionKey]?.enabled === false;
                              return (
                                <div className={`flex items-center gap-3 p-2.5 bg-muted/30 rounded-md border border-border ${isOff ? "opacity-50" : ""}`} data-testid={`section-order-row-${sectionKey}`}>
                                  <button {...dragHandleProps}><DragHandleIcon /></button>
                                  <Icon size={14} className="text-muted-foreground shrink-0" />
                                  <span className="text-sm font-medium flex-1">{meta.label}</span>
                                  {isOff && <span className="text-[10px] text-muted-foreground italic">ausgeblendet</span>}
                                </div>
                              );
                            }}
                          />
                        </>
                      );
                    })()}
                  </EditorSection>
                )}

                {/* TESTIMONIALS — Kundenstimmen */}
                {showSection("testimonials") && (
                <EditorSection
                  icon={Quote}
                  title="Kundenstimmen"
                  subtitle={`${editContent.testimonials.length} Testimonial${editContent.testimonials.length === 1 ? "" : "s"}${editContent.testimonials.length < 6 ? " — bis zu 6 möglich" : ""}`}
                  defaultOpen={false}
                  testid="testimonials"
                  enabled={isSectionEnabled("testimonials")}
                  onToggleEnabled={(v) => setSectionEnabled("testimonials", v)}
                >
                  {editContent.testimonials.length > 0 && editContent.testimonials.every(t => /Sarah|Michael|Kevin|Melanie|Sandra/.test(t.author || "")) && (
                    <div className="mb-2 p-2 bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-900 rounded-md text-[11px] text-violet-900 dark:text-violet-200" data-testid="testimonials-ai-warning">
                      ℹ️ Diese Testimonials wirken wie KI-Generiert (Standard-Namen). Überleg, ob du echte Google-Reviews einblenden willst (Firma-&gt;Google-Review-Link).
                    </div>
                  )}
                  <div className="flex justify-end -mt-1 mb-1">
                    {editContent.testimonials.length < 6 && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => setEditContent(prev => ({ ...prev, testimonials: [...prev.testimonials, { text: "", author: "", role: "", rating: 5 }] }))} className="h-7 px-2 text-xs rounded-md gap-1" data-testid="add-testimonial-btn">
                        <Plus size={11} /> Testimonial hinzufügen
                      </Button>
                    )}
                  </div>
                  {editContent.testimonials.length === 0 && (
                    <p className="text-xs text-muted-foreground italic py-3 text-center">Noch keine Testimonials. Klicke auf „Hinzufügen".</p>
                  )}
                  <SortableList
                    items={editContent.testimonials.map((_, i) => i)}
                    onReorder={(newOrder) => setEditContent(prev => ({ ...prev, testimonials: newOrder.map(i => prev.testimonials[i]) }))}
                    itemKey="testimonial"
                    renderItem={(idx, { dragHandleProps }) => {
                      const t = editContent.testimonials[idx];
                      return (
                        <div className="p-3 bg-muted/30 rounded-md space-y-2 border border-border" data-testid={`testimonial-card-${idx}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <button {...dragHandleProps}><DragHandleIcon /></button>
                              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Stimme {idx + 1}</span>
                            </div>
                            <button type="button" onClick={() => setEditContent(prev => ({ ...prev, testimonials: prev.testimonials.filter((_, i2) => i2 !== idx) }))} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`remove-testimonial-${idx}`}>
                              <Trash2 size={12} />
                            </button>
                          </div>
                          <Textarea
                            value={t.text}
                            onChange={e => setEditContent(prev => ({ ...prev, testimonials: prev.testimonials.map((x, i2) => i2 === idx ? { ...x, text: e.target.value } : x) }))}
                            rows={3}
                            placeholder="„Die Fahrstunden waren super strukturiert und die Fahrlehrer extrem geduldig."
                            className="rounded-md text-sm"
                            data-testid={`edit-testimonial-text-${idx}`}
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              value={t.author}
                              onChange={e => setEditContent(prev => ({ ...prev, testimonials: prev.testimonials.map((x, i2) => i2 === idx ? { ...x, author: e.target.value } : x) }))}
                              placeholder="Name (z.B. Sarah M.)"
                              className="rounded-md text-sm"
                              data-testid={`edit-testimonial-author-${idx}`}
                            />
                            <Input
                              value={t.role || ""}
                              onChange={e => setEditContent(prev => ({ ...prev, testimonials: prev.testimonials.map((x, i2) => i2 === idx ? { ...x, role: e.target.value } : x) }))}
                              placeholder="Rolle (optional)"
                              className="rounded-md text-sm"
                              data-testid={`edit-testimonial-role-${idx}`}
                            />
                          </div>
                        </div>
                      );
                    }}
                  />
                </EditorSection>
                )}

                {/* PRICING — Pakete / Preise */}
                {showSection("pricing") && (
                <EditorSection
                  icon={Tag}
                  title="Pakete & Preise"
                  subtitle={`${editContent.pricing.length} Paket${editContent.pricing.length === 1 ? "" : "e"}${editContent.pricing.length < 4 ? " — bis zu 4 möglich" : ""}`}
                  defaultOpen={false}
                  testid="pricing"
                  enabled={isSectionEnabled("pricing")}
                  onToggleEnabled={(v) => setSectionEnabled("pricing", v)}
                >
                  <div className="flex justify-end -mt-1 mb-1">
                    {editContent.pricing.length < 4 && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => setEditContent(prev => ({ ...prev, pricing: [...prev.pricing, { name: "", price: "", period: "", description: "", features: [], highlighted: false, cta: "Mehr erfahren" }] }))} className="h-7 px-2 text-xs rounded-md gap-1" data-testid="add-pricing-btn">
                        <Plus size={11} /> Paket hinzufügen
                      </Button>
                    )}
                  </div>
                  {editContent.pricing.length === 0 && (
                    <p className="text-xs text-muted-foreground italic py-3 text-center">Noch keine Pakete. Klicke auf „Hinzufügen".</p>
                  )}
                  <SortableList
                    items={editContent.pricing.map((_, i) => i)}
                    onReorder={(newOrder) => setEditContent(prev => ({ ...prev, pricing: newOrder.map(i => prev.pricing[i]) }))}
                    itemKey="pricing"
                    renderItem={(idx, { dragHandleProps }) => {
                      const p = editContent.pricing[idx];
                      return (
                        <div className="p-3 bg-muted/30 rounded-md space-y-2 border border-border" data-testid={`pricing-card-${idx}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <button {...dragHandleProps}><DragHandleIcon /></button>
                              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Paket {idx + 1}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <label className="flex items-center gap-1 text-[10px] text-muted-foreground cursor-pointer" title="Hervorgehoben (empfohlen)">
                                <input
                                  type="checkbox"
                                  checked={!!p.highlighted}
                                  onChange={e => setEditContent(prev => ({ ...prev, pricing: prev.pricing.map((x, i2) => i2 === idx ? { ...x, highlighted: e.target.checked } : x) }))}
                                  data-testid={`edit-pricing-highlighted-${idx}`}
                                />
                                Highlight
                              </label>
                              <button type="button" onClick={() => setEditContent(prev => ({ ...prev, pricing: prev.pricing.filter((_, i2) => i2 !== idx) }))} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`remove-pricing-${idx}`}>
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <Input value={p.name} onChange={e => setEditContent(prev => ({ ...prev, pricing: prev.pricing.map((x, i2) => i2 === idx ? { ...x, name: e.target.value } : x) }))} placeholder="Paketname" className="rounded-md text-sm col-span-2" data-testid={`edit-pricing-name-${idx}`} />
                            <Input value={p.price} onChange={e => setEditContent(prev => ({ ...prev, pricing: prev.pricing.map((x, i2) => i2 === idx ? { ...x, price: e.target.value } : x) }))} placeholder="z.B. 49 €" className="rounded-md text-sm" data-testid={`edit-pricing-price-${idx}`} />
                          </div>
                          <Input value={p.period || ""} onChange={e => setEditContent(prev => ({ ...prev, pricing: prev.pricing.map((x, i2) => i2 === idx ? { ...x, period: e.target.value } : x) }))} placeholder='Zeitraum (z.B. „pro Fahrstunde")' className="rounded-md text-sm" data-testid={`edit-pricing-period-${idx}`} />
                          <Textarea value={p.description} onChange={e => setEditContent(prev => ({ ...prev, pricing: prev.pricing.map((x, i2) => i2 === idx ? { ...x, description: e.target.value } : x) }))} rows={2} placeholder="Kurz-Beschreibung" className="rounded-md text-sm" data-testid={`edit-pricing-description-${idx}`} />
                          <div>
                            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Features (je Zeile eine)</Label>
                            <Textarea
                              value={(p.features || []).join("\n")}
                              onChange={e => setEditContent(prev => ({ ...prev, pricing: prev.pricing.map((x, i2) => i2 === idx ? { ...x, features: e.target.value.split("\n").map(s => s.trim()).filter(Boolean) } : x) }))}
                              rows={3}
                              placeholder={"PKW-Ausbildung\n12 Fahrstunden\nPrüfungsbegleitung"}
                              className="rounded-md text-sm mt-1"
                              data-testid={`edit-pricing-features-${idx}`}
                            />
                          </div>
                        </div>
                      );
                    }}
                  />
                </EditorSection>
                )}

                {/* TEAM-MEMBERS */}
                {showSection("team-members") && (
                <EditorSection
                  icon={Users}
                  title="Team-Mitglieder"
                  subtitle={`${editContent.team_members.length} Mitglied${editContent.team_members.length === 1 ? "" : "er"}${editContent.team_members.length < 8 ? " — bis zu 8 möglich" : ""}`}
                  defaultOpen={false}
                  testid="team-members"
                  enabled={isSectionEnabled("team_members")}
                  onToggleEnabled={(v) => setSectionEnabled("team_members", v)}
                >
                  <div className="flex justify-end -mt-1 mb-1">
                    {editContent.team_members.length < 8 && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => setEditContent(prev => ({ ...prev, team_members: [...prev.team_members, { name: "", role: "", bio: "", photo_url: "" }] }))} className="h-7 px-2 text-xs rounded-md gap-1" data-testid="add-team-member-btn">
                        <Plus size={11} /> Mitglied hinzufügen
                      </Button>
                    )}
                  </div>
                  {editContent.team_members.length === 0 && (
                    <p className="text-xs text-muted-foreground italic py-3 text-center">Noch keine Team-Mitglieder. Wurden keine über die Website gefunden, kannst du sie hier manuell ergänzen.</p>
                  )}
                  <SortableList
                    items={editContent.team_members.map((_, i) => i)}
                    onReorder={(newOrder) => setEditContent(prev => ({ ...prev, team_members: newOrder.map(i => prev.team_members[i]) }))}
                    itemKey="team-member"
                    renderItem={(idx, { dragHandleProps }) => {
                      const m = editContent.team_members[idx];
                      return (
                        <div className="p-3 bg-muted/30 rounded-md space-y-2 border border-border" data-testid={`team-member-card-${idx}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <button {...dragHandleProps}><DragHandleIcon /></button>
                              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Mitglied {idx + 1}</span>
                            </div>
                            <button type="button" onClick={() => setEditContent(prev => ({ ...prev, team_members: prev.team_members.filter((_, i2) => i2 !== idx) }))} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`remove-team-member-${idx}`}>
                              <Trash2 size={12} />
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <Input value={m.name} onChange={e => setEditContent(prev => ({ ...prev, team_members: prev.team_members.map((x, i2) => i2 === idx ? { ...x, name: e.target.value } : x) }))} placeholder="Vorname Nachname" className="rounded-md text-sm" data-testid={`edit-team-name-${idx}`} />
                            <Input value={m.role} onChange={e => setEditContent(prev => ({ ...prev, team_members: prev.team_members.map((x, i2) => i2 === idx ? { ...x, role: e.target.value } : x) }))} placeholder="Rolle (z.B. Fahrlehrer B)" className="rounded-md text-sm" data-testid={`edit-team-role-${idx}`} />
                          </div>
                          <Textarea value={m.bio} onChange={e => setEditContent(prev => ({ ...prev, team_members: prev.team_members.map((x, i2) => i2 === idx ? { ...x, bio: e.target.value } : x) }))} rows={2} placeholder="Kurzbiografie (optional)" className="rounded-md text-sm" data-testid={`edit-team-bio-${idx}`} />
                          <Input value={m.photo_url} onChange={e => setEditContent(prev => ({ ...prev, team_members: prev.team_members.map((x, i2) => i2 === idx ? { ...x, photo_url: e.target.value } : x) }))} placeholder="Foto-URL (optional)" className="rounded-md text-sm font-mono" data-testid={`edit-team-photo-${idx}`} />
                          {m.photo_url && (
                            <img src={m.photo_url} alt={m.name || "Team-Mitglied"} className="w-16 h-16 object-cover rounded-md border border-border" onError={(e) => { e.target.style.display = "none"; }} />
                          )}
                        </div>
                      );
                    }}
                  />
                </EditorSection>
                )}

                {/* FOOTER SOCIAL-LINKS */}
                {showSection("social") && (
                <EditorSection
                  icon={Share2}
                  title="Footer – Social Media"
                  subtitle="Instagram, Facebook, LinkedIn, TikTok – Links im Footer"
                  defaultOpen={false}
                  testid="social"
                  enabled={isSectionEnabled("social_links")}
                  onToggleEnabled={(v) => setSectionEnabled("social_links", v)}
                >
                  <div className="space-y-2">
                    {["instagram", "facebook", "linkedin", "tiktok", "youtube", "twitter"].map(platform => (
                      <div key={platform} className="grid grid-cols-[88px,1fr] gap-2 items-center">
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground capitalize">{platform}</span>
                        <Input
                          value={editContent.social_links?.[platform] || ""}
                          onChange={e => setEditContent(prev => ({ ...prev, social_links: { ...(prev.social_links || {}), [platform]: e.target.value } }))}
                          placeholder={`https://${platform}.com/…`}
                          className="rounded-md text-sm font-mono"
                          data-testid={`edit-social-${platform}`}
                        />
                      </div>
                    ))}
                    <p className="text-[10px] text-muted-foreground italic pt-1">Leeres Feld = Icon wird ausgeblendet.</p>
                  </div>
                </EditorSection>
                )}

                {/* CONTACT — Quick-Modus */}
                {showSection("contact") && (
                <EditorSection
                  icon={Mail}
                  title="Kontakt-Bereich"
                  subtitle="Überschrift + optional individuelle Daten (überschreibt Firmen-Stammdaten)"
                  quick
                  testid="contact"
                  enabled={isSectionEnabled("contact")}
                  onToggleEnabled={(v) => setSectionEnabled("contact", v)}
                  headerAction={
                    <SectionRegenButton
                      projectId={id}
                      section="contact"
                      onRegenerated={(data) => setEditContent(prev => ({ ...prev, contact: { ...prev.contact, heading: data?.heading || prev.contact.heading } }))}
                    />
                  }
                >
                  <div>
                    <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Überschrift</Label>
                    <Input value={editContent.contact.heading} onChange={e => updateField("contact", "heading", e.target.value)} className="rounded-md mt-1" data-testid="edit-contact-heading" />
                  </div>
                  <div className="pt-3 mt-2 border-t border-border">
                    <Label className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                      Eigene Kontaktdaten
                      <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground bg-muted px-1.5 py-0.5 rounded-sm">optional</span>
                    </Label>
                    <p className="text-[11px] text-muted-foreground mt-1 italic">
                      Leer = automatisch aus den Firmen-Stammdaten ({company?.email || "—"} · {company?.phone || "—"} · {company?.city || "—"}).
                      Felder hier überschreiben die Stammdaten <strong>nur für diese Demo</strong>.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">E-Mail</Label>
                      <Input
                        type="email"
                        value={editContent.contact.email || ""}
                        onChange={e => updateField("contact", "email", e.target.value)}
                        placeholder={company?.email || "kontakt@firma.de"}
                        className="rounded-md mt-1 text-sm"
                        data-testid="edit-contact-email"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Telefon</Label>
                      <Input
                        value={editContent.contact.phone || ""}
                        onChange={e => updateField("contact", "phone", e.target.value)}
                        placeholder={company?.phone || "+49 123 456789"}
                        className="rounded-md mt-1 text-sm"
                        data-testid="edit-contact-phone"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Adresse (Straße + Hausnummer)</Label>
                      <Input
                        value={editContent.contact.address || ""}
                        onChange={e => updateField("contact", "address", e.target.value)}
                        placeholder={company?.address || "Musterstraße 1"}
                        className="rounded-md mt-1 text-sm"
                        data-testid="edit-contact-address"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Stadt / PLZ</Label>
                      <Input
                        value={editContent.contact.city || ""}
                        onChange={e => updateField("contact", "city", e.target.value)}
                        placeholder={company?.city || "10115 Berlin"}
                        className="rounded-md mt-1 text-sm"
                        data-testid="edit-contact-city"
                      />
                    </div>
                  </div>
                </EditorSection>
                )}

                {showSection("contact") && (
                  <WhatsAppEditor
                    value={editContent.whatsapp}
                    onChange={(next) => setEditContent(prev => ({ ...prev, whatsapp: next }))}
                    companyName={company?.name || ""}
                    companyWhatsapp={company?.whatsapp || ""}
                  />
                )}

                {/* CUSTOM PAGES — auf Custom-Page-Tab IMMER sichtbar (auch im Schnell-Modus),
                    da der User die Seite ja explizit angeklickt hat. */}
                {showSection("custom-pages") && (currentEditPage.startsWith("custom:") || editorMode === "pro") && (
                  <EditorSection
                    icon={FileText}
                    title={currentEditPage.startsWith("custom:")
                      ? `Seite: ${currentEditPage.slice(7)}`
                      : "Eigene Seiten (Subpages)"}
                    subtitle={currentEditPage.startsWith("custom:")
                      ? "Hero, Stats, Features, Content & CTA dieser Unterseite"
                      : `${editContent.custom_pages.length} Seiten — Karriere, FAQ, Anfahrt etc.`}
                    defaultOpen={true}
                    testid="custom-pages"
                  >
                    <div className="flex items-center gap-1.5 justify-end -mt-1 mb-2">
                      <Button type="button" size="sm" variant="outline" onClick={openAiCreateModal} className="h-7 px-2 text-xs rounded-md gap-1 bg-gradient-to-r from-violet-600 to-indigo-600 text-white border-0 hover:opacity-90" data-testid="add-custom-page-ai-btn">
                        <Sparkles size={11} /> Mit KI erstellen
                      </Button>
                      <Button type="button" size="sm" variant="ghost" onClick={addCustomPage} className="h-7 px-2 text-xs rounded-md gap-1" data-testid="add-custom-page-btn">
                        <Plus size={11} /> Leer hinzufügen
                      </Button>
                    </div>
                    {/* Page-Type-Info-Banner: erklärt welche Datenquelle die gerenderte
                        Subpage tatsächlich befüllt (z.B. „Referenzen" rendert testimonials). */}
                    {(() => {
                      if (!currentEditPage.startsWith("custom:")) return null;
                      const slug = currentEditPage.slice(7);
                      const cp = (editContent.custom_pages || []).find(p => p.slug === slug);
                      if (!cp) return null;
                      const ptype = detectPageType(cp);
                      if (ptype === "rich-landing") return null;
                      const info = PAGE_TYPE_INFO[ptype];
                      if (!info) return null;
                      return (
                        <div className="mb-3 p-3 rounded-md border border-violet-300/40 bg-gradient-to-br from-violet-50/80 to-indigo-50/40 dark:from-violet-950/30 dark:to-indigo-950/20" data-testid={`page-type-banner-${ptype}`}>
                          <div className="flex items-start gap-2">
                            <div className="p-1 rounded-sm bg-violet-600 text-white shrink-0 mt-0.5">
                              <Sparkles size={11} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-bold text-violet-900 dark:text-violet-200">{info.label}</div>
                              <p className="text-[11px] text-violet-800/80 dark:text-violet-300/80 mt-0.5 leading-relaxed">{info.desc}</p>
                              {info.section && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCurrentEditPage("index");
                                    setTimeout(() => {
                                      window.dispatchEvent(new CustomEvent("editor-section-open", { detail: { testid: info.section } }));
                                      const el = document.getElementById(`editor-section-${info.section}`);
                                      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                                    }, 120);
                                  }}
                                  className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-violet-600 text-white text-[11px] font-semibold hover:bg-violet-700 transition-colors"
                                  data-testid={`page-type-jump-${info.section}`}
                                >
                                  → Zur „{info.label.replace("-Layout", "").trim()}"-Bearbeitung
                                </button>
                              )}
                              {/* Fahrschul-Preis-Matrix Tier-Override */}
                              {ptype === "fahrschule-pricing" && (() => {
                                const currentTier = (cp.pricing_tier || "").trim().toLowerCase() || "auto";
                                const setTier = (tier) => {
                                  setEditContent(prev => ({
                                    ...prev,
                                    custom_pages: prev.custom_pages.map(c => c.slug === slug ? { ...c, pricing_tier: tier === "auto" ? "" : tier } : c),
                                  }));
                                };
                                const TIERS = [
                                  { id: "auto",     label: "Auto", desc: "aus Brand-DNA abgeleitet" },
                                  { id: "sparsam",  label: "Sparsam", desc: "ländlich · €1.700-2.400" },
                                  { id: "standard", label: "Standard", desc: "Stadt · €2.500-3.400" },
                                  { id: "premium",  label: "Premium", desc: "Großstadt · €3.500+" },
                                ];
                                return (
                                  <div className="mt-3 pt-3 border-t border-violet-300/40 dark:border-violet-700/40" data-testid="fahrschule-pricing-tier-editor">
                                    <div className="text-[11px] font-bold text-violet-900 dark:text-violet-200 mb-1.5">Preis-Niveau überschreiben</div>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                                      {TIERS.map(t => {
                                        const active = currentTier === t.id;
                                        return (
                                          <button
                                            key={t.id}
                                            type="button"
                                            onClick={() => setTier(t.id)}
                                            data-testid={`fahrschule-tier-${t.id}-btn`}
                                            className={`group flex flex-col items-start gap-0.5 px-2 py-1.5 rounded-md border text-left transition-all ${
                                              active
                                                ? "bg-violet-600 border-violet-700 text-white shadow-sm"
                                                : "bg-white/70 dark:bg-violet-950/40 border-violet-300/50 dark:border-violet-700/50 hover:border-violet-500 text-violet-900 dark:text-violet-200"
                                            }`}
                                          >
                                            <span className="text-[11px] font-bold leading-tight">{t.label}</span>
                                            <span className={`text-[9px] leading-tight ${active ? "text-violet-100" : "text-violet-700/70 dark:text-violet-400/70"}`}>{t.desc}</span>
                                          </button>
                                        );
                                      })}
                                    </div>
                                    <p className="text-[10px] text-violet-700/80 dark:text-violet-400/70 mt-1.5 italic">Speichern + „Vorschau aktualisieren" um Änderung zu sehen.</p>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    <p className="text-[11px] text-muted-foreground italic mb-1">Jede Seite ist eine vollwertige Landing-Page mit Hero, Stats, Features, Content und CTA.</p>
                    {editContent.custom_pages.length === 0 && (
                      <div className="py-6 text-center border border-dashed border-border rounded-md">
                        <FileText size={20} className="mx-auto mb-2 text-muted-foreground" />
                        <p className="text-xs text-muted-foreground">Noch keine eigenen Seiten angelegt.</p>
                        <p className="text-[11px] text-muted-foreground mt-1">Tipp: Klicke oben auf „Mit KI erstellen" – 3 Sätze Briefing reichen.</p>
                      </div>
                    )}
                    <SortableList
                      items={editContent.custom_pages
                        .map((_, i) => i)
                        .filter(i => {
                          if (!currentEditPage.startsWith("custom:")) return true;
                          const slug = currentEditPage.slice(7);
                          return (editContent.custom_pages[i]?.slug || "") === slug;
                        })}
                      onReorder={(newOrder) => setEditContent(prev => ({ ...prev, custom_pages: newOrder.map(i => prev.custom_pages[i]) }))}
                      itemKey="custom-page"
                      renderItem={(i, { dragHandleProps }) => {
                      const p = editContent.custom_pages[i];
                      if (!p) return null;
                      const reservedTitle = ["impressum","datenschutz","meilensteine","startseite","home"].includes((p.title || "").toLowerCase().trim());
                      return (
                      <details key={i} className="bg-muted/20 rounded-md border border-border group" data-testid={`custom-page-${i}`} open={true}>
                        <summary className="flex items-center justify-between p-3 cursor-pointer list-none">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <button {...dragHandleProps} onClick={(e)=>e.preventDefault()}><DragHandleIcon /></button>
                            <FileText size={13} className="text-muted-foreground shrink-0" />
                            <span className="text-sm font-semibold truncate">{p.title || `Seite ${i + 1}`}</span>
                            <span className="text-[10px] font-mono text-muted-foreground">/{p.slug}</span>
                            {p.in_nav && <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-sm">In Nav</span>}
                          </div>
                          {p.slug && project?.public_slug && (
                            <button
                              type="button"
                              onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                // Bug-Fix C: Vorschau direkt auf diese Subpage umschalten,
                                // damit der User seine Edits ohne Tab-Hopping prüfen kann.
                                handlePageSwitch(p.slug);
                                setViewMode("preview");
                                toast.success(`Vorschau wechselt zu „${p.title || p.slug}"`);
                              }}
                              className="text-[10px] uppercase tracking-wider text-primary hover:text-primary/80 px-2 py-0.5 rounded-sm hover:bg-primary/10 transition-colors mr-1"
                              title="Diese Seite in der Vorschau anzeigen"
                              data-testid={`custom-page-preview-${i}`}
                            >
                              Ansehen →
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={e => { e.preventDefault(); e.stopPropagation(); removeCustomPage(i); }}
                            className="text-muted-foreground hover:text-destructive transition-colors p-1"
                            data-testid={`remove-custom-page-${i}`}
                          >
                            <Trash2 size={12} />
                          </button>
                        </summary>
                        <div className="p-3 pt-0 space-y-4 border-t border-border">
                          {reservedTitle && (
                            <div className="p-2.5 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-sm text-xs text-amber-900 dark:text-amber-200">
                              ⚠ Dieser Titel ist reserviert. „Impressum" & „Datenschutz" erscheinen automatisch im <strong>Footer</strong>. Bitte wähle einen anderen Titel.
                            </div>
                          )}
                          <div className="grid grid-cols-[1fr_auto] gap-2 pt-3">
                            <Input value={p.title} onChange={e => updateCustomPage(i, "title", e.target.value)} placeholder="Nav-Titel" className="rounded-md text-sm" data-testid={`custom-page-title-${i}`} />
                            <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none whitespace-nowrap px-2">
                              <input type="checkbox" checked={!!p.in_nav} onChange={e => updateCustomPage(i, "in_nav", e.target.checked)} className="w-3.5 h-3.5 accent-primary" data-testid={`custom-page-nav-${i}`} />
                              In Navigation
                            </label>
                          </div>
                          <div className="grid grid-cols-[100px_1fr] gap-2 items-center">
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">URL-Slug</span>
                            <Input value={p.slug} onChange={e => updateCustomPage(i, "slug", slugify(e.target.value))} placeholder="z.B. karriere" className="rounded-md text-sm font-mono h-8" data-testid={`custom-page-slug-${i}`} />
                          </div>

                          {/* PHASE 3: Block-Toggles + Reihenfolge pro Subpage.
                              Toggles funktionieren für alle Layouts (via _strip_disabled_subpage_blocks),
                              aber Reihenfolge gilt nur für rich-landing. Für spezialisierte Layouts
                              (story, team, faq, gallery, …) zeigen wir einen Hinweis. */}
                          {(() => {
                            const ptype = detectPageType(p);
                            const isRichLanding = ptype === "rich-landing";
                            const RELEVANT_BLOCKS = {
                              "rich-landing": ["stats", "features", "content", "cta", "faqs", "gallery"],
                              "story":        ["cta"],
                              "team":         ["cta"],
                              "faq":          ["faqs", "cta"],
                              "testimonials": ["cta"],
                              "gallery":      ["gallery", "cta"],
                              "services-detail": ["cta"],
                              "blog":         ["cta"],
                              "blog-post":    ["cta"],
                              "careers":      ["cta"],
                              "directions":   ["cta"],
                              "project-inquiry": ["cta"],
                            };
                            const relevant = RELEVANT_BLOCKS[ptype] || ["stats", "features", "content", "cta", "faqs", "gallery"];
                            const orderToShow = isRichLanding
                              ? getCpBlockOrder(i)
                              : getCpBlockOrder(i).filter(b => relevant.includes(b));
                            return (
                          <details className="bg-background rounded-md border border-border/60 group/bo">
                            <summary className="flex items-center gap-2 p-2.5 cursor-pointer list-none text-xs font-semibold uppercase tracking-widest text-foreground">
                              <Layers size={12} />
                              Block-{isRichLanding ? "Reihenfolge & " : ""}Toggles
                              <ChevronDown size={12} className="ml-auto opacity-60 group-open/bo:rotate-180 transition-transform" />
                            </summary>
                            <div className="p-2.5 pt-0 space-y-1">
                              <p className="text-[10px] text-muted-foreground italic mb-2">
                                {isRichLanding
                                  ? "Ziehe Blöcke in die gewünschte Reihenfolge oder schalte sie per Toggle aus. Der Hero bleibt immer oben."
                                  : `${PAGE_TYPE_INFO[ptype]?.label || "Dieses Layout"} rendert nur spezifische Blöcke. Die hier angezeigten Toggles wirken auf diese Subpage; Reihenfolge ist nicht anpassbar.`}
                              </p>
                              <SortableList
                                items={orderToShow}
                                onReorder={isRichLanding ? (newOrder) => setCpBlockOrder(i, newOrder) : () => {}}
                                itemKey={`cp-${i}-block-order`}
                                renderItem={(blockKey, { dragHandleProps: dhp }) => {
                                  const LABELS = {
                                    stats: "Key-Facts (Kacheln)",
                                    features: "Features / USPs",
                                    content: "Content-Block",
                                    cta: "CTA-Abschluss",
                                    faqs: "FAQs",
                                    gallery: "Galerie",
                                  };
                                  const on = isCpBlockOn(i, blockKey);
                                  return (
                                    <div className={`flex items-center gap-2 p-1.5 bg-muted/40 rounded-sm border border-border ${on ? "" : "opacity-50"}`} data-testid={`cp-${i}-block-${blockKey}-row`}>
                                      {isRichLanding && <button {...dhp}><DragHandleIcon /></button>}
                                      <span className="text-[11px] flex-1">{LABELS[blockKey]}</span>
                                      <button
                                        type="button"
                                        role="switch"
                                        aria-checked={on}
                                        onClick={() => setCpBlockOn(i, blockKey, !on)}
                                        className={`relative inline-flex items-center h-4 w-7 rounded-full transition-colors ${on ? "bg-emerald-500" : "bg-muted-foreground/30"}`}
                                        data-testid={`cp-${i}-block-${blockKey}-toggle`}
                                      >
                                        <span className={`inline-block h-3 w-3 bg-white rounded-full shadow transform transition-transform ${on ? "translate-x-3.5" : "translate-x-0.5"}`} />
                                      </button>
                                    </div>
                                  );
                                }}
                              />
                            </div>
                          </details>
                            );
                          })()}
                          <div className="space-y-2 pt-2 border-t border-border/50">
                            <Label className="text-[11px] font-bold uppercase tracking-widest text-foreground">Hero-Bereich</Label>
                            <Input value={p.hero.title} onChange={e => updateCustomPageNested(i, "hero", "title", e.target.value)} placeholder="Hero-Titel" className="rounded-md text-sm" data-testid={`cp-hero-title-${i}`} />
                            <Textarea value={p.hero.subtitle} onChange={e => updateCustomPageNested(i, "hero", "subtitle", e.target.value)} placeholder="Hero-Untertitel (1-2 Sätze)" rows={2} className="rounded-md text-sm" data-testid={`cp-hero-subtitle-${i}`} />
                            <Input value={p.hero.cta} onChange={e => updateCustomPageNested(i, "hero", "cta", e.target.value)} placeholder="Button-Text (z.B. 'Jetzt bewerben')" className="rounded-md text-sm" data-testid={`cp-hero-cta-${i}`} />
                            <div className="pt-1.5 border-t border-border/40">
                              <CtaActionEditor
                                value={p.hero.cta_action}
                                onChange={(next) => updateCustomPageNested(i, "hero", "cta_action", next)}
                                customPages={editContent.custom_pages || []}
                                companyPhone={company?.phone || editContent.contact?.phone || ""}
                                companyEmail={company?.email || editContent.contact?.email || ""}
                                testidPrefix={`cp-hero-cta-action-${i}`}
                                compact
                              />
                            </div>
                            {/* Hero-Bild – wird bei Story- & Rich-Landing-Layouts gerendert.
                                Fallback bei story-Layout: content.images.about (aus „Logo & Bilder"
                                im Globals-Tab). Dieses Feld überschreibt den Fallback pro Subpage. */}
                            {(() => {
                              const ptype = detectPageType(p);
                              const fallbackAbout = (editContent?.images || {}).about || "";
                              const effectiveUrl = p.hero?.image || (ptype === "story" ? fallbackAbout : "");
                              return (
                                <div className="pt-2 border-t border-border/40 space-y-2" data-testid={`cp-hero-image-block-${i}`}>
                                  <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Hero-Bild (optional)</Label>
                                  <div className="flex items-start gap-2">
                                    <div className="w-20 h-14 rounded-sm border border-border bg-muted/30 overflow-hidden flex items-center justify-center shrink-0">
                                      {effectiveUrl ? (
                                        <img src={effectiveUrl} alt="Hero" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                                      ) : (
                                        <ImageIcon size={14} className="text-muted-foreground" />
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0 space-y-1">
                                      <Input
                                        value={p.hero?.image || ""}
                                        onChange={e => updateCustomPageNested(i, "hero", "image", e.target.value)}
                                        placeholder={ptype === "story" ? `Standard: About-Bild (${fallbackAbout ? "gesetzt" : "Branchen-Pack"})` : "Bild-URL (optional)"}
                                        className="rounded-md text-xs font-mono h-8"
                                        data-testid={`cp-hero-image-${i}`}
                                      />
                                      <div className="flex items-center gap-2 flex-wrap">
                                        {ptype === "story" && (
                                          <button
                                            type="button"
                                            onClick={async () => {
                                              const tId = toast.loading("Würfle neues About-Bild…");
                                              try {
                                                const res = await axios.post(`${API}/projects/${id}/reroll-image?slot=about`, null, { withCredentials: true });
                                                setEditContent(prev => ({ ...prev, images: { ...(prev.images || {}), about: res.data.url } }));
                                                refreshPreview();
                                                toast.dismiss(tId);
                                                toast.success("Neues Bild gewählt");
                                              } catch (err) {
                                                toast.dismiss(tId);
                                                toast.error(err.response?.data?.detail || "Würfeln fehlgeschlagen");
                                              }
                                            }}
                                            className="text-[10px] text-primary hover:text-primary/80 inline-flex items-center gap-1"
                                            data-testid={`cp-reroll-about-image-${i}`}
                                          >
                                            <Shuffle size={10} /> About-Bild würfeln
                                          </button>
                                        )}
                                        {p.hero?.image && (
                                          <button
                                            type="button"
                                            onClick={() => updateCustomPageNested(i, "hero", "image", "")}
                                            className="text-[10px] text-muted-foreground hover:text-destructive inline-flex items-center gap-1"
                                            data-testid={`cp-hero-image-clear-${i}`}
                                          >
                                            <Trash2 size={10} /> Entfernen
                                          </button>
                                        )}
                                      </div>
                                      <p className="text-[10px] text-muted-foreground leading-tight">
                                        {ptype === "story"
                                          ? 'Wenn leer: das „About-Bild" aus „Logo & Bilder" (Globals) wird gerendert. Hier setzt du ein eigenes Bild nur für diese Subpage.'
                                          : "Optional — nur einige Layouts rendern ein Hero-Bild."}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                          <div className="space-y-2 pt-2 border-t border-border/50">
                            <Label className="text-[11px] font-bold uppercase tracking-widest text-foreground">Key-Facts (4 Kacheln)</Label>
                            <div className="grid grid-cols-2 gap-2">
                              {p.stats.map((s, si) => (
                                <div key={si} className="grid grid-cols-[80px_1fr] gap-1.5">
                                  <Input value={s.value} onChange={e => updateCustomPageArrayItem(i, "stats", si, "value", e.target.value)} placeholder="95%" className="rounded-md text-xs font-mono h-8" data-testid={`cp-stat-value-${i}-${si}`} />
                                  <Input value={s.label} onChange={e => updateCustomPageArrayItem(i, "stats", si, "label", e.target.value)} placeholder="Label" className="rounded-md text-xs h-8" data-testid={`cp-stat-label-${i}-${si}`} />
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="space-y-2 pt-2 border-t border-border/50">
                            <Label className="text-[11px] font-bold uppercase tracking-widest text-foreground">Features / USPs (bis 3)</Label>
                            {p.features.slice(0, 3).map((f, fi) => (
                              <div key={fi} className="space-y-1 p-2 bg-background rounded-md border border-border/60">
                                <Input value={f.title} onChange={e => updateCustomPageArrayItem(i, "features", fi, "title", e.target.value)} placeholder={`Feature ${fi + 1} Titel`} className="rounded-md text-xs h-8" data-testid={`cp-feature-title-${i}-${fi}`} />
                                <Textarea value={f.description} onChange={e => updateCustomPageArrayItem(i, "features", fi, "description", e.target.value)} placeholder="Kurze Beschreibung" rows={2} className="rounded-md text-xs" data-testid={`cp-feature-desc-${i}-${fi}`} />
                              </div>
                            ))}
                          </div>
                          <div className="space-y-2 pt-2 border-t border-border/50">
                            <Label className="text-[11px] font-bold uppercase tracking-widest text-foreground">Content-Block</Label>
                            <Input value={p.content.heading} onChange={e => updateCustomPageNested(i, "content", "heading", e.target.value)} placeholder="Überschrift" className="rounded-md text-sm" data-testid={`cp-content-heading-${i}`} />
                            <Textarea value={p.content.text} onChange={e => updateCustomPageNested(i, "content", "text", e.target.value)} placeholder="Haupttext. Leerzeile trennt Absätze." rows={5} className="rounded-md text-sm" data-testid={`cp-content-text-${i}`} />
                          </div>
                          <div className="space-y-2 pt-2 border-t border-border/50">
                            <Label className={`text-[11px] font-bold uppercase tracking-widest text-foreground ${isCpBlockOn(i, "cta") ? "" : "opacity-40"}`}>
                              CTA-Abschluss {!isCpBlockOn(i, "cta") && <span className="text-[10px] font-normal text-muted-foreground ml-1 normal-case">(ausgeblendet)</span>}
                            </Label>
                            <Input value={p.cta.heading} onChange={e => updateCustomPageNested(i, "cta", "heading", e.target.value)} placeholder="Abschluss-Headline" className="rounded-md text-sm" data-testid={`cp-cta-heading-${i}`} />
                            <Input value={p.cta.button} onChange={e => updateCustomPageNested(i, "cta", "button", e.target.value)} placeholder="Button-Text" className="rounded-md text-sm" data-testid={`cp-cta-button-${i}`} />
                            <div className="pt-1.5 border-t border-border/40">
                              <CtaActionEditor
                                value={p.cta.action}
                                onChange={(next) => updateCustomPageNested(i, "cta", "action", next)}
                                customPages={editContent.custom_pages || []}
                                companyPhone={company?.phone || editContent.contact?.phone || ""}
                                companyEmail={company?.email || editContent.contact?.email || ""}
                                testidPrefix={`cp-cta-action-${i}`}
                                compact
                              />
                            </div>
                          </div>

                          {/* PHASE 3: FAQs-Editor mit Drag&Drop */}
                          <div className="space-y-2 pt-2 border-t border-border/50">
                            <div className="flex items-center justify-between">
                              <Label className={`text-[11px] font-bold uppercase tracking-widest text-foreground ${isCpBlockOn(i, "faqs") ? "" : "opacity-40"}`}>
                                FAQs {!isCpBlockOn(i, "faqs") && <span className="text-[10px] font-normal text-muted-foreground ml-1 normal-case">(ausgeblendet)</span>}
                              </Label>
                              <Button type="button" size="sm" variant="ghost" onClick={() => addCpFaq(i)} className="h-6 px-2 text-[10px] rounded-md gap-1" data-testid={`cp-add-faq-${i}`}>
                                <Plus size={10} /> FAQ
                              </Button>
                            </div>
                            {(p.faqs || []).length === 0 ? (
                              <p className="text-[11px] text-muted-foreground italic py-2">Keine FAQs. Klick auf „+ FAQ" um eine Frage hinzuzufügen.</p>
                            ) : (
                              <SortableList
                                items={(p.faqs || []).map((_, fi) => fi)}
                                onReorder={(newOrder) => setEditContent(prev => {
                                  const pages = [...prev.custom_pages];
                                  pages[i] = { ...pages[i], faqs: newOrder.map(fi => pages[i].faqs[fi]) };
                                  return { ...prev, custom_pages: pages };
                                })}
                                itemKey={`cp-${i}-faq`}
                                renderItem={(fi, { dragHandleProps: dhp }) => {
                                  const f = p.faqs[fi] || {};
                                  return (
                                    <div className="p-2 bg-background rounded-md border border-border/60 space-y-1" data-testid={`cp-${i}-faq-card-${fi}`}>
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-1.5">
                                          <button {...dhp}><DragHandleIcon /></button>
                                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">FAQ {fi + 1}</span>
                                        </div>
                                        <button type="button" onClick={() => removeCpFaq(i, fi)} className="text-muted-foreground hover:text-destructive p-0.5" data-testid={`cp-${i}-faq-remove-${fi}`}><Trash2 size={11} /></button>
                                      </div>
                                      <Input value={f.q || f.question || ""} onChange={e => updateCustomPageArrayItem(i, "faqs", fi, "q", e.target.value)} placeholder="Frage" className="rounded-md text-xs h-8" data-testid={`cp-${i}-faq-q-${fi}`} />
                                      <Textarea value={f.a || f.answer || ""} onChange={e => updateCustomPageArrayItem(i, "faqs", fi, "a", e.target.value)} placeholder="Antwort" rows={2} className="rounded-md text-xs" data-testid={`cp-${i}-faq-a-${fi}`} />
                                    </div>
                                  );
                                }}
                              />
                            )}
                          </div>

                          {/* PHASE 3: Gallery-Editor */}
                          <div className="space-y-2 pt-2 border-t border-border/50">
                            <div className="flex items-center justify-between">
                              <Label className={`text-[11px] font-bold uppercase tracking-widest text-foreground ${isCpBlockOn(i, "gallery") ? "" : "opacity-40"}`}>
                                Galerie {!isCpBlockOn(i, "gallery") && <span className="text-[10px] font-normal text-muted-foreground ml-1 normal-case">(ausgeblendet)</span>}
                              </Label>
                              <Button type="button" size="sm" variant="ghost" onClick={() => setEditContent(prev => {
                                const pages = [...prev.custom_pages];
                                pages[i] = { ...pages[i], gallery: [...(pages[i].gallery || []), { url: "", caption: "" }] };
                                return { ...prev, custom_pages: pages };
                              })} className="h-6 px-2 text-[10px] rounded-md gap-1" data-testid={`cp-add-gallery-${i}`}>
                                <Plus size={10} /> Bild
                              </Button>
                            </div>
                            {(p.gallery || []).length === 0 ? (
                              <p className="text-[11px] text-muted-foreground italic py-2">Keine Bilder.</p>
                            ) : (
                              <SortableList
                                items={(p.gallery || []).map((_, gi) => gi)}
                                onReorder={(newOrder) => setEditContent(prev => {
                                  const pages = [...prev.custom_pages];
                                  pages[i] = { ...pages[i], gallery: newOrder.map(gi => pages[i].gallery[gi]) };
                                  return { ...prev, custom_pages: pages };
                                })}
                                itemKey={`cp-${i}-gallery`}
                                renderItem={(gi, { dragHandleProps: dhp }) => {
                                  const g = p.gallery[gi] || {};
                                  const url = typeof g === "string" ? g : (g.url || g.src || "");
                                  const caption = typeof g === "string" ? "" : (g.caption || "");
                                  return (
                                    <div className="p-2 bg-background rounded-md border border-border/60 space-y-1" data-testid={`cp-${i}-gallery-card-${gi}`}>
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-1.5">
                                          <button {...dhp}><DragHandleIcon /></button>
                                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Bild {gi + 1}</span>
                                        </div>
                                        <button type="button" onClick={() => setEditContent(prev => {
                                          const pages = [...prev.custom_pages];
                                          pages[i] = { ...pages[i], gallery: (pages[i].gallery || []).filter((_, idx) => idx !== gi) };
                                          return { ...prev, custom_pages: pages };
                                        })} className="text-muted-foreground hover:text-destructive p-0.5" data-testid={`cp-${i}-gallery-remove-${gi}`}><Trash2 size={11} /></button>
                                      </div>
                                      <Input
                                        value={url}
                                        onChange={e => {
                                          const newVal = e.target.value;
                                          setEditContent(prev => {
                                            const pages = [...prev.custom_pages];
                                            const arr = [...(pages[i].gallery || [])];
                                            arr[gi] = { ...(typeof arr[gi] === "object" ? arr[gi] : {}), url: newVal };
                                            pages[i] = { ...pages[i], gallery: arr };
                                            return { ...prev, custom_pages: pages };
                                          });
                                        }}
                                        placeholder="Bild-URL"
                                        className="rounded-md text-xs font-mono h-8"
                                        data-testid={`cp-${i}-gallery-url-${gi}`}
                                      />
                                      <Input
                                        value={caption}
                                        onChange={e => {
                                          const newVal = e.target.value;
                                          setEditContent(prev => {
                                            const pages = [...prev.custom_pages];
                                            const arr = [...(pages[i].gallery || [])];
                                            arr[gi] = { ...(typeof arr[gi] === "object" ? arr[gi] : { url: arr[gi] }), caption: newVal };
                                            pages[i] = { ...pages[i], gallery: arr };
                                            return { ...prev, custom_pages: pages };
                                          });
                                        }}
                                        placeholder="Bildunterschrift (optional)"
                                        className="rounded-md text-xs h-8"
                                        data-testid={`cp-${i}-gallery-caption-${gi}`}
                                      />
                                      {url && <img src={url} alt={caption || "Galerie"} className="w-full h-24 object-cover rounded-md border border-border" onError={(e) => { e.target.style.display = "none"; }} />}
                                    </div>
                                  );
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </details>
                      );
                    }}
                    />
                  </EditorSection>
                )}

                {/* SUBPAGES BULK GENERATION — nur Pro-Modus, in Custom-List-Tab */}
                {editorMode === "pro" && showSection("subpages-ai") && (
                  <EditorSection
                    icon={Sparkles}
                    title="Neue Unterseiten mit KI"
                    subtitle="Mehrere Subpages auf einmal generieren lassen"
                    defaultOpen={false}
                    testid="subpages-ai"
                  >
                    <div data-testid="subpages-bulk-section" className="space-y-3">
                      <p className="text-[11px] text-muted-foreground italic">Wähle Vorlagen oder definiere eigene Unterseiten mit Briefing. Die KI baut sie in einem Rutsch und hängt sie an deine bestehenden Seiten an.</p>
                      <SubpagesEditor
                        subpages={plannedSubpages}
                        setSubpages={setPlannedSubpages}
                        onGenerateAll={handleGenerateSubpages}
                        generating={generatingSubpages}
                      />
                    </div>
                  </EditorSection>
                )}

                {/* IMPRESSUM — eigene Seite */}
                {editorMode === "pro" && showSection("impressum") && (
                  <EditorSection
                    icon={FileText}
                    title="Impressum"
                    subtitle="Pflichtangaben gemäß § 5 TMG"
                    defaultOpen={true}
                    testid="impressum"
                  >
                    <p className="text-[11px] text-muted-foreground italic">Die fertige Seite muss vor dem Live-Gang von einem Fachanwalt geprüft werden.</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Firma</Label>
                        <Input value={editContent.legal.impressum.company_name} onChange={e => updateImpressum("company_name", e.target.value)} className="rounded-md mt-1 text-sm" data-testid="edit-imp-company" />
                      </div>
                      <div>
                        <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Inhaber / GF</Label>
                        <Input value={editContent.legal.impressum.owner} onChange={e => updateImpressum("owner", e.target.value)} className="rounded-md mt-1 text-sm" data-testid="edit-imp-owner" />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Anschrift</Label>
                        <Input value={editContent.legal.impressum.address} onChange={e => updateImpressum("address", e.target.value)} className="rounded-md mt-1 text-sm" data-testid="edit-imp-address" />
                      </div>
                      <div>
                        <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Telefon</Label>
                        <Input value={editContent.legal.impressum.phone} onChange={e => updateImpressum("phone", e.target.value)} className="rounded-md mt-1 text-sm" data-testid="edit-imp-phone" />
                      </div>
                      <div>
                        <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">E-Mail</Label>
                        <Input value={editContent.legal.impressum.email} onChange={e => updateImpressum("email", e.target.value)} className="rounded-md mt-1 text-sm" data-testid="edit-imp-email" />
                      </div>
                      <div>
                        <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">USt-ID</Label>
                        <Input value={editContent.legal.impressum.vat_id} onChange={e => updateImpressum("vat_id", e.target.value)} className="rounded-md mt-1 text-sm" data-testid="edit-imp-vat" />
                      </div>
                      <div>
                        <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Handelsregister</Label>
                        <Input value={editContent.legal.impressum.register} onChange={e => updateImpressum("register", e.target.value)} className="rounded-md mt-1 text-sm" data-testid="edit-imp-register" />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Registergericht</Label>
                        <Input value={editContent.legal.impressum.register_court} onChange={e => updateImpressum("register_court", e.target.value)} className="rounded-md mt-1 text-sm" data-testid="edit-imp-court" />
                      </div>
                    </div>
                  </EditorSection>
                )}

                {/* DATENSCHUTZ — eigene Seite */}
                {editorMode === "pro" && showSection("datenschutz") && (
                  <EditorSection
                    icon={FileText}
                    title="Datenschutz"
                    subtitle="DSGVO-Vorlage + branchen-spezifische Ergänzungen"
                    defaultOpen={false}
                    testid="datenschutz"
                  >
                    <p className="text-[11px] text-muted-foreground italic">Die Standard-Struktur wird automatisch generiert. Ergänze hier deine branchen-spezifischen Hinweise.</p>
                    <div>
                      <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Einleitung</Label>
                      <Textarea value={editContent.legal.datenschutz.intro} onChange={e => updateDatenschutz("intro", e.target.value)} rows={3} className="rounded-md mt-1 text-sm" data-testid="edit-ds-intro" />
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Ergänzende Angaben (optional)</Label>
                      <Textarea value={editContent.legal.datenschutz.text} onChange={e => updateDatenschutz("text", e.target.value)} rows={4} className="rounded-md mt-1 text-sm" placeholder="z.B. Cookies, Analytics, Newsletter..." data-testid="edit-ds-text" />
                    </div>
                  </EditorSection>
                )}

                <div className="sticky bottom-0 bg-card -mx-0 px-4 pt-3 pb-3 border-t border-border flex items-center justify-end gap-2 rounded-b-lg shadow-lg" data-testid="editor-action-bar">
                  <Button type="button" variant="outline" onClick={handleSuggestContent} disabled={suggestingContent || savingContent} className="rounded-md text-sm gap-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white border-0 hover:opacity-90 mr-auto" data-testid="suggest-content-bottom-btn">
                    {suggestingContent ? <><RefreshCw size={14} className="animate-spin" /> KI denkt nach...</> : <><Sparkles size={14} /> KI-Vorschlag einfügen</>}
                  </Button>
                  <button
                    type="button"
                    onClick={handleUndo}
                    disabled={editHistory.current.pointer <= 0}
                    title="Rückgängig (⌘Z)"
                    className="flex items-center justify-center w-8 h-8 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    data-testid="undo-btn"
                  >
                    <Undo2 size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={handleRedo}
                    disabled={editHistory.current.pointer >= editHistory.current.stack.length - 1}
                    title="Wiederholen (⇧⌘Z)"
                    className="flex items-center justify-center w-8 h-8 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    data-testid="redo-btn"
                  >
                    <Redo2 size={14} />
                  </button>
                  <Button type="button" variant="outline" onClick={() => setViewMode("preview")} className="rounded-md text-sm" data-testid="cancel-edit-bottom-btn">
                    Abbrechen
                  </Button>
                  <Button type="button" onClick={handleSaveContent} disabled={savingContent} className="rounded-md text-sm gap-2" data-testid="save-content-bottom-btn">
                    {savingContent ? <><RefreshCw size={14} className="animate-spin" /> Speichert...</> : <><Save size={14} /> Speichern & Vorschau aktualisieren</>}
                  </Button>
                </div>
                </div>
              </div>
            </div>
            {/* Right column: embedded Live-Preview iframe (split view) */}
            {splitView && previewUrl && (
              <div className="border-l border-border bg-muted/10 flex flex-col min-h-0" data-testid="split-live-preview">
                <div className="flex items-center justify-between px-3 py-1.5 border-b border-border bg-card shrink-0">
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Live · {currentPage}</span>
                  <button
                    type="button"
                    onClick={() => { const k = Date.now(); setRefreshKey(k); setPreviewUrl(buildPreviewUrl(id, currentPage, k, inlineEditMode)); }}
                    className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                    title="Vorschau neu laden"
                    data-testid="split-preview-refresh"
                  >
                    <RefreshCw size={11} />
                  </button>
                </div>
                <iframe
                  key={`split-${refreshKey}`}
                  src={`${buildPreviewUrl(id, currentPage, refreshKey, inlineEditMode)}&live=1#mp_${currentPage}`}
                  title="Split Live-Vorschau"
                  className="flex-1 w-full border-0 bg-white"
                  data-testid="split-preview-iframe"
                />
              </div>
            )}
            </div>
          )}
        </div>
      </div>

      {/* AI Create Page Modal */}
      {aiModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !aiCreating && setAiModalOpen(false)}
          data-testid="ai-create-page-modal"
        >
          <div
            className="bg-card border border-border rounded-sm w-full max-w-xl shadow-2xl overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-gradient-to-r from-violet-600/10 to-indigo-600/10">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-violet-600" />
                <h3 className="font-heading text-sm font-bold">Neue Seite mit KI erstellen</h3>
              </div>
              {!aiCreating && (
                <button
                  type="button"
                  onClick={() => setAiModalOpen(false)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="ai-modal-close"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            <div className="p-5 space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Briefing *</Label>
                <Textarea
                  value={aiBrief}
                  onChange={e => setAiBrief(e.target.value)}
                  placeholder="Beschreibe in 1-3 Sätzen, worum es auf der Seite gehen soll. Beispiel: &quot;Wir suchen neue Azubis, Schwerpunkt Ausbildung zum Friseur und Karrieremöglichkeiten bei uns im Salon.&quot;"
                  rows={5}
                  className="rounded-sm mt-1 text-sm"
                  data-testid="ai-brief-textarea"
                  disabled={aiCreating}
                  autoFocus
                />
                <p className="text-[11px] text-muted-foreground mt-1">KI erstellt daraus eine komplette Seite – Titel, Überschrift, Einleitung und Text.</p>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Titel-Vorschlag (optional)</Label>
                <Input
                  value={aiTitle}
                  onChange={e => setAiTitle(e.target.value)}
                  placeholder="z.B. Karriere (leer lassen = KI schlägt vor)"
                  className="rounded-sm mt-1 text-sm"
                  data-testid="ai-title-input"
                  disabled={aiCreating}
                />
              </div>

              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={aiInNav}
                  onChange={e => setAiInNav(e.target.checked)}
                  className="w-4 h-4 accent-primary"
                  data-testid="ai-in-nav-checkbox"
                  disabled={aiCreating}
                />
                In der Hauptnavigation anzeigen
              </label>

              <div className="p-3 bg-muted/40 rounded-sm border border-border text-xs text-muted-foreground">
                <strong className="text-foreground">Tipp:</strong> Je konkreter das Briefing, desto besser die Seite.
                Nenne konkrete Dinge (Zielgruppe, Angebote, USPs, Tonalität), die auf der Seite vorkommen sollen.
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border bg-muted/20">
              <Button
                type="button"
                variant="outline"
                onClick={() => setAiModalOpen(false)}
                disabled={aiCreating}
                className="rounded-sm text-sm"
                data-testid="ai-modal-cancel"
              >
                Abbrechen
              </Button>
              <Button
                type="button"
                onClick={handleAiCreatePage}
                disabled={aiCreating || aiBrief.trim().length < 5}
                className="rounded-sm text-sm gap-2 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white"
                data-testid="ai-modal-submit"
              >
                {aiCreating ? (
                  <><RefreshCw size={14} className="animate-spin" /> KI schreibt... (~10s)</>
                ) : (
                  <><Sparkles size={14} /> Seite erstellen</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Public Share Modal */}
      {shareOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShareOpen(false)} data-testid="share-modal">
          <div className="bg-card rounded-sm shadow-2xl max-w-md w-full border border-border" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-border flex items-center justify-between">
              <h3 className="text-base font-bold uppercase tracking-widest">Für Kunden freigeben</h3>
              <button onClick={() => setShareOpen(false)} className="text-muted-foreground hover:text-foreground" data-testid="share-close-btn"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={!!project?.public_enabled} onChange={e => togglePublicShare(e.target.checked)} className="mt-1 w-4 h-4 accent-primary" data-testid="share-toggle" />
                <span className="text-sm">
                  <strong className="block">Öffentlichen Link aktivieren</strong>
                  <span className="text-xs text-muted-foreground">Jeder mit diesem Link sieht die Website ohne Login.</span>
                </span>
              </label>
              {project?.public_enabled && publicShareUrl && (
                <>
                  <div className="p-3 bg-muted/40 rounded-sm border border-border">
                    <code className="text-[11px] break-all text-foreground" data-testid="share-url">{publicShareUrl}</code>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={copyShareLink} className="rounded-sm gap-1.5 flex-1" data-testid="share-copy-btn">
                      <Copy size={11} /> Link kopieren
                    </Button>
                    <a href={publicShareUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm border border-border hover:border-foreground transition-colors" data-testid="share-open-btn">
                      <ExternalLink size={11} /> Öffnen
                    </a>
                  </div>
                  <button type="button" onClick={() => togglePublicShare(true, true)} className="text-xs text-muted-foreground hover:text-destructive transition-colors" data-testid="share-rotate-btn">
                    Neuen Link erzeugen (alter wird ungültig)
                  </button>
                </>
              )}
              <p className="text-[11px] text-muted-foreground italic border-t border-border pt-3">
                Hinweis: Auf Freigabe-Seiten ist die Terminbuchung (für Beauty/Restaurant/Fitness) aktiv. Buchungen landen in der Firmen-Detailseite.
              </p>
            </div>
          </div>
        </div>
      )}

      <StockVideoPicker
        open={showVideoPicker}
        onClose={() => setShowVideoPicker(false)}
        onSelect={(v) => {
          updateField("hero", "video_url", v.url);
          // Wenn noch kein Hero-Image vorhanden, Thumbnail als Poster setzen
          if (!editContent.images?.hero) {
            setEditContent(prev => ({ ...prev, images: { ...(prev.images || {}), hero: v.thumbnail } }));
          }
          toast.success(`Video „${v.title}" übernommen. Speichern nicht vergessen!`);
        }}
        industryHint={(company?.industry || "").toLowerCase()}
      />

      {/* Floating Mini-Preview — nur im Edit-Modus aktiv, schwebt über allem.
          Wird automatisch deaktiviert wenn Split-View aktiv ist (kein Doppel-Preview). */}
      {viewMode === "edit" && previewUrl && miniPreviewOpen && !splitView && (
        <FloatingMiniPreview
          previewBase={previewUrl}
          refreshKey={refreshKey}
          onClose={toggleMiniPreview}
        />
      )}

      {/* Logo-Suggestions-Modal */}
      <LogoSuggestionsPicker
        open={showLogoPicker}
        projectId={id}
        onClose={() => setShowLogoPicker(false)}
        onSelect={(dataUrl) => {
          setEditContent(prev => ({ ...prev, logo_url: dataUrl }));
          refreshPreview();
        }}
      />

      {/* Subpages-Suggestions-Modal */}
      <SubpageSuggestionsModal
        open={showSubpagesModal}
        projectId={id}
        API={API}
        onClose={() => setShowSubpagesModal(false)}
        onConfirm={async (selected) => {
          if (!selected || selected.length === 0) {
            toast.info("Keine Subpages ausgewählt — Demo wird nur mit Hauptseite gebaut");
            return;
          }
          try {
            const res = await axios.post(
              `${API}/projects/${id}/queue-subpages`,
              { subpages: selected, mode: "append" },
              { withCredentials: true }
            );
            const added = res.data?.added || 0;
            const total = res.data?.queued_total || 0;
            if (added > 0) {
              toast.success(`${added} Subpage${added === 1 ? "" : "s"} in Queue (gesamt: ${total}). Klick „Neu erstellen" um sie zu generieren.`);
            } else {
              toast.info("Alle ausgewählten Subpages waren bereits in der Queue oder existieren");
            }
            // Project-State neu laden damit der Status-Strip die neue Queue zeigt
            const p = await axios.get(`${API}/projects/${id}`, { withCredentials: true });
            setProject(p.data);
          } catch (err) {
            toast.error(err.response?.data?.detail || "Subpages-Queue konnte nicht aktualisiert werden");
          }
        }}
      />

      {/* Neue-Seite-Modal: Vorgefertigte Templates für schnelles Anlegen */}
      {showNewPageModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowNewPageModal(false)} data-testid="new-page-modal">
          <div className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="font-bold text-base">Neue Unterseite hinzufügen</h3>
                <p className="text-xs text-muted-foreground">Wähle eine Vorlage oder leg eine leere Seite an. Du kannst danach alles bearbeiten.</p>
              </div>
              <button type="button" onClick={() => setShowNewPageModal(false)} className="p-1.5 rounded-sm hover:bg-muted transition-colors" data-testid="new-page-modal-close">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 overflow-y-auto grid grid-cols-2 gap-3">
              {[
                { slug: "anfahrt", title: "Anfahrt", icon: "📍", description: "Adresse + Anfahrtsbeschreibung + Karte. Ideal für lokale Geschäfte." },
                { slug: "preise", title: "Preise", icon: "💰", description: "Detaillierte Preistabelle mit Leistungsumfang." },
                { slug: "team", title: "Über das Team", icon: "👥", description: "Vorstellung aller Mitarbeiter mit Bildern und Bios." },
                { slug: "faq", title: "Häufige Fragen", icon: "❓", description: "FAQ-Liste mit ausklappbaren Antworten." },
                { slug: "karriere", title: "Karriere", icon: "💼", description: "Stellenangebote + Bewerbungsformular." },
                { slug: "blog", title: "Blog / News", icon: "📰", description: "Artikel-Übersicht mit Vorschau-Kacheln." },
                { slug: "erfolge", title: "Erfolgsgeschichten", icon: "🏆", description: "Case-Studies & echte Kunden-Testimonials." },
                { slug: "", title: "Leere Seite", icon: "📄", description: "Komplett leer — du baust die Inhalte selbst." },
              ].map((t, ti) => (
                <button
                  key={t.slug || `empty-${ti}`}
                  type="button"
                  onClick={async () => {
                    setShowNewPageModal(false);
                    let slug = t.slug;
                    let title = t.title;
                    let brief = t.description;
                    if (!slug) {
                      slug = prompt('Slug der neuen Seite (z.B. "karriere"):', "")?.toLowerCase().trim().replace(/[^a-z0-9-]/g, "-");
                      if (!slug) return;
                      title = slug.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                      brief = prompt(`Was soll auf der Seite "${title}" stehen? (1-2 Sätze)`, "") || "";
                    }
                    // Duplicate-Check
                    const exists = (editContent.custom_pages || []).some(p => (p.slug || "").toLowerCase() === slug);
                    if (exists) {
                      toast.error(`Seite "${slug}" existiert bereits`);
                      setCurrentEditPage(`custom:${slug}`);
                      return;
                    }
                    // Auto-Fill via KI: ruft den existierenden ai-create-page-Endpoint
                    // an, der eine vollwertige Subpage mit Hero+Stats+Features+CTA
                    // im Brand-DNA-Stil der Firma erzeugt.
                    const toastId = toast.loading(`KI erstellt „${title}"… (15-30s)`);
                    try {
                      const res = await axios.post(
                        `${API}/projects/${id}/ai-create-page`,
                        { brief: brief || `Eine ${title}-Seite die zur Marke passt.`, title, in_nav: true },
                        { withCredentials: true, timeout: 180000 }
                      );
                      toast.dismiss(toastId);
                      // Project + Preview neu laden
                      const pRes = await axios.get(`${API}/projects/${id}`, { withCredentials: true });
                      setProject(pRes.data);
                      // editContent neu laden mit den frisch generierten Daten
                      const newContent = pRes.data.generated_content || pRes.data.content;
                      if (newContent) setEditContent(deepClone(newContent));
                      const newSlug = res.data.slug || slug;
                      setCurrentEditPage(`custom:${newSlug}`);
                      await loadPreview(id, newSlug);
                      toast.success(`Seite „${title}" mit KI erstellt — passt zur Marke!`);
                    } catch (err) {
                      toast.dismiss(toastId);
                      toast.error(err.response?.data?.detail || "KI-Generierung fehlgeschlagen — Seite wird leer angelegt");
                      // Fallback: leere Seite anlegen
                      const newPage = {
                        slug,
                        title: title || slug,
                        in_nav: true,
                        hero: { eyebrow: "", title: title || slug, subtitle: brief || "" },
                        stats: [], features: [], content_blocks: [], gallery: [],
                        cta: { heading: "Bereit loszulegen?", button: "Jetzt Kontakt aufnehmen" },
                      };
                      setEditContent(prev => ({ ...prev, custom_pages: [...(prev.custom_pages || []), newPage] }));
                      setCurrentEditPage(`custom:${slug}`);
                    }
                  }}
                  className="text-left p-4 rounded-md border border-border hover:border-foreground/40 hover:bg-muted/30 transition-all"
                  data-testid={`new-page-template-${t.slug || "empty"}`}
                >
                  <div className="text-2xl mb-1">{t.icon}</div>
                  <div className="font-semibold text-sm">{t.title}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{t.description}</div>
                </button>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-border bg-muted/20 flex items-center justify-end">
              <button type="button" onClick={() => setShowNewPageModal(false)} className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="new-page-modal-cancel">Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
